import { noteServerVersion } from "./version";
import { getCsrfToken } from "./auth";
import {
  workConnectorsApi,
  briefApi,
  type WorkConnectorRow,
  type WorkRunRow,
  type BriefPayload,
  type BriefSections,
} from "../anchors/settings/connections/api";

export type { WorkConnectorRow, WorkRunRow, BriefPayload, BriefSections };

/** Engagement row as returned by the V2.0 stream endpoint. Matches
 *  the engagements table columns plus an optional `signals` column
 *  (comma-joined active signal_kinds). */
export interface EngagementRow {
  id: string;
  user_id: string;
  kind: string;
  title: string;
  description: string | null;
  status: string;
  scope: string | null;
  due_at: string | null;
  related_people_json: string | null;
  source_packets_json: string | null;
  detection_source: string;
  detection_confidence: number | null;
  detection_reason: string | null;
  last_activity_at: string | null;
  activity_count: number;
  is_test: number;
  created_at: string;
  updated_at: string;
  decided_at: string | null;
  signals?: string | null;
  /** Wave 1B: SQLite EXISTS() yields 0/1. Set on RECENTLY ACTIVE rows
   *  whose engagement carries a p0_security_others packet (a security
   *  alert for someone else's account) so the SPA can show a small
   *  "for another account" chip. */
  security_others?: number;
}

/** Response shape for GET /api/engagements/stream — the four slots
 *  per V2.0 §3.5 + §3.8 (Engagement Stream). */
export interface EngagementStreamResponse {
  ok: boolean;
  slots: {
    up_next: EngagementRow[];
    awaiting_you: EngagementRow[];
    going_cold: EngagementRow[];
    recent: EngagementRow[];
  };
  /** Wave F: per-person display lookup keyed by person_id. Populated
   *  from `engagements.related_people_json`. Lets the SPA render
   *  participant chips without a second round-trip. */
  people_lookup?: Record<string, {
    display_name: string | null;
    role: string | null;
    /** Inferred/auto-confirmed relationship for the inline confirm
     *  pill on the engagement card. Absent when there is no dotted
     *  relationship to confirm. */
    relationship?: PersonInferredRelationship | null;
  }>;
  meta: {
    live_window_from: string;
    live_window_to: string;
    total_active_in_window: number;
  };
}

function csrfHeaders(): Record<string, string> {
  const t = getCsrfToken();
  return t ? { "X-CSRF-Token": t } : {};
}

/** Hard timeout for boot-blocking GET fetches. The structural rule: a
 *  page whose first render depends on an API call must give that call
 *  a timeout, because a hung request renders as an indefinite
 *  Loading... that the user cannot escape. 12 s is long enough to
 *  absorb a Cloudflare-tunnel cold start + a slow daemon path, short
 *  enough to surface an error before the user thinks the app is
 *  broken. Wrap a fetch like:
 *
 *      const res = await fetch(url, withFetchTimeout({...}));
 *
 *  On timeout, AbortController aborts the fetch; the caller's catch
 *  path sees a DOMException("AbortError"). json() re-throws and the
 *  React component's .catch(setError) renders an error box instead
 *  of staying on Loading. */
const BOOT_FETCH_TIMEOUT_MS = 12_000;

function withFetchTimeout(init: RequestInit = {}, timeoutMs = BOOT_FETCH_TIMEOUT_MS): RequestInit {
  if (typeof AbortController === "undefined") return init;
  const ctrl = new AbortController();
  // If the caller already passed a signal, chain it: abort our ctrl
  // when their signal aborts (covers route unmount / strict-mode
  // cleanup) and vice-versa.
  const callerSignal = init.signal;
  if (callerSignal) {
    if (callerSignal.aborted) ctrl.abort();
    else callerSignal.addEventListener("abort", () => ctrl.abort(), { once: true });
  }
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  // Best-effort: clear the timer when the abort fires for any reason
  // so we don't keep a stale handle in long-lived single-page sessions.
  ctrl.signal.addEventListener("abort", () => clearTimeout(timer), { once: true });
  return { ...init, signal: ctrl.signal };
}

/** Friendly fetch wrapper that:
 *   - records X-App-Version on every response so the version watcher
 *     learns the running server version without a separate poll
 *   - synthesises a friendly error when an upstream proxy returns HTML
 *     instead of our backend's JSON (Cloudflare 5xx, cloudflared
 *     disconnect, nginx default error page)
 *   - throws an Error with a readable message on non-2xx for the caller
 *     to render in a toast or dialog */
async function json<T>(res: Response): Promise<T> {
  const seen = res.headers.get("x-app-version");
  if (seen) noteServerVersion(seen);

  // 401 = auth required. Bounce the user to the SPA's /login route which
  // shows a "Sign in with Google" button. Preserve the originally
  // intended URL so /login can return them after a successful sign-in.
  if (res.status === 401 && typeof window !== "undefined") {
    if (!window.location.pathname.startsWith("/login")) {
      const next = window.location.pathname + window.location.search;
      window.location.replace(`/login?next=${encodeURIComponent(next)}`);
      throw new Error("Redirecting to login");
    }
  }

  if (!res.ok) {
    const ct = res.headers.get("content-type") || "";
    const text = await res.text().catch(() => "");
    const looksLikeHtml =
      ct.includes("text/html") ||
      /^\s*<(?:!doctype|html|head|body|title|script)/i.test(text);
    if (looksLikeHtml) {
      throw new Error(friendlyMessageForStatus(res.status));
    }
    // Try to lift an `error` field out of a JSON body. Don't wrap in a
    // try/catch around the throw - the previous version did, which
    // swallowed the throw and then leaked the raw response text into
    // the UI (manifested as "{"error":"not_found"}" rendered verbatim
    // in the Engagement Stream content area).
    let parsedErrorCode: string | null = null;
    if (text) {
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed.error === "string") {
          parsedErrorCode = parsed.error;
        } else if (parsed && parsed.error && typeof parsed.error.message === "string") {
          parsedErrorCode = parsed.error.message;
        }
      } catch {
        /* not JSON; fall through to the status-friendly message */
      }
    }
    // Always surface a human-readable message based on the HTTP status.
    // Append the error code in parentheses when we have one, but never
    // dump raw response text into the UI.
    const friendly = friendlyMessageForStatus(res.status);
    throw new Error(parsedErrorCode ? `${friendly} (${parsedErrorCode})` : friendly);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function friendlyMessageForStatus(status: number): string {
  if (status === 401) return "Sign in to continue.";
  if (status === 403) return "You do not have access to that.";
  if (status === 404) return "Not found.";
  if (status === 502 || status === 503 || status === 504) return "The server is unreachable. Try again in a moment.";
  if (status >= 500) return "Server error. Try again.";
  return `Request failed (${status}).`;
}

export interface VersionPayload {
  version: string;
  pkgVersion?: string;
  gitSha?: string;
  buildTime?: string;
}

export interface ContextNow {
  context_id: string;
  source: string;
  location_name: string | null;
  time_of_day_bucket: string;
  day_of_week: number;
  is_weekend: number;
  weather_condition: string | null;
  weather_temp_f: number | null;
}

export interface NamedPlace {
  place_id: string;
  name: string;
  lat: number;
  lng: number;
  radius_m: number;
  kind: string;
  is_inferred: number;
  confirmed_at: string | null;
  created_at: string;
}

export interface ContextPrefs {
  locationEnabled: boolean;
  weatherEnabled: boolean;
  activityEnabled: boolean;
}

export interface PendingNotification {
  notification_id: string;
  source_surfacing_id: string | null;
  kind: string;
  title: string;
  body: string | null;
  scope: string | null;
  context_match_json: string | null;
  match_score: number | null;
  created_at: string;
}

export interface AdminResetResult {
  ok: boolean;
  dry_run: boolean;
  snapshot_path: string | null;
  tables_cleared: string[];
  tables_to_clear?: string[];
  tables_kept: string[];
  tables_unclassified: string[];
  row_counts_before: Record<string, number>;
  row_counts_after: Record<string, number>;
  error?: string;
}

export interface AdminSnapshot {
  path: string;
  name: string;
  size_bytes: number;
  created_at: string;
}

// Cached per-thread conversation summary (ambient #3). Named
// ThreadSummary to avoid colliding with the chat ConversationSummary.
export interface ThreadSummary {
  id: string;
  user_id: string;
  thread_key: string;
  thread_kind: "gmail" | "whatsapp" | "sms" | "linkedin" | "outlook" | "teams" | "other";
  summary_md: string;
  key_points: string[];
  participants: Array<{ name: string; role: string }>;
  commitments: Array<{ who: string; what: string; by_when: string | null }>;
  sentiment: "positive" | "neutral" | "negative" | "mixed";
  last_packet_at: string | null;
  last_summarized_at: string | null;
  packet_count_at_last_summary: number;
  model_used: string | null;
  scope_set: string[];
}

export interface DashboardToday {
  date: string;
  greeting: string;
  /** Track C Layer 4: human context strip ("Friday evening, at
   *  Home. 72F clear"). Null when no context captured. */
  context_now?: string | null;
  stats: {
    capturesToday: number;
    openTasks: number;
    agentRunsToday: number;
    unreadMatches: number;
    totalCaptures: number;
  };
  brief: null | {
    headline: string | null;
    body: string | null;
    generatedAt: string;
    model: string;
    route: string;
  };
  recent: CaptureRow[];
  upcomingDeadlines: { id: string; title: string; due_at: string | null; scope: string }[];
  mentions: MonitorMatch[];
  deadlines: MonitorMatch[];
  suggestions: MonitorMatch[];
  /** Ambient backlog #4: top forgotten threads (prominence > 0.7). */
  forgottenThreads?: ForgottenBriefItem[];
  /** Subscription audit: top 1-2 high-confidence cancel suggestions. */
  subscriptionCancelSuggestions?: SubscriptionCancelSuggestion[];
  /** End-of-day reflection nudge: true after 8pm local with no
   *  reflection saved for today yet. */
  reflectReminder?: boolean;
  /** Commitment tracker: top commitments due in the next 48 hours. */
  comingDue?: Commitment[];
}

export interface ReflectionRow {
  id: string;
  reflection_date: string;
  mood: "great" | "good" | "neutral" | "low" | "rough" | null;
  energy_level: number | null;
  highlights_md: string | null;
  lows_md: string | null;
  grateful_for_md: string | null;
  tomorrow_focus_md: string | null;
  open_loops_md: string | null;
  tags: string[];
  captured_at: string;
}
export interface ReflectionInput {
  reflection_date?: string;
  mood?: string | null;
  energy_level?: number | null;
  highlights_md?: string | null;
  lows_md?: string | null;
  grateful_for_md?: string | null;
  tomorrow_focus_md?: string | null;
  open_loops_md?: string | null;
}
export interface ReflectionStats {
  days: number;
  count: number;
  mood_distribution: Record<string, number>;
  average_energy: number | null;
  streak: number;
}

export interface WikiCandidate {
  kind: string;
  id: string;
  display_name: string;
}
export type WikiResolveResult =
  | { kind: string; id: string; display_name: string; ambiguous?: false }
  | { kind: "ambiguous"; ambiguous: true; candidates: WikiCandidate[] };
export interface UnifiedActionRow {
  id: string;
  source_table: string;
  kind: string;
  title: string;
  summary: string;
  created_at: string;
  status:
    | "pending"
    | "accepted"
    | "dismissed"
    | "executed"
    | "failed"
    | "snoozed";
  link: string;
  payload_json: string | null;
  scope_set: string[];
  /** Q42: id of the originating packet so the row can open the
   *  universal packet viewer. Null when the row doesn't trace to a
   *  packet (forgotten threads, agentic runs, etc.). */
  source_packet_id: string | null;
}

export interface WikiBacklinkRow {
  source_kind: string;
  source_id: string;
  source_title: string | null;
  preview: string | null;
  target_display: string | null;
  updated_at: string;
}

export type CommitmentStatus = "open" | "in_progress" | "completed" | "dropped";

export interface Commitment {
  id: string;
  title: string;
  who: string | null;
  what: string;
  by_when: string | null;
  status: CommitmentStatus;
  scope: string | null;
  detection_source: string;
  source_thread_key: string | null;
  person_id: string | null;
  person_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface CommitmentTrends {
  counts: Record<CommitmentStatus, number>;
  total: number;
  completion_rate: number;
  most_overdue: Commitment[];
  recently_fulfilled: Commitment[];
}

export interface Goal {
  id: string;
  title: string;
  status: CommitmentStatus;
  target_date: string | null;
  scope: string | null;
  created_at: string;
  linked_commitments: Commitment[];
}

export interface SubscriptionCancelSuggestion {
  id: string;
  vendor_name: string;
  amount_cents: number;
  currency: string;
  cadence: string;
  reason: string;
  confidence: number;
  recommend_cancel: boolean;
}

export interface SubscriptionUsageSignals {
  last_login_at: string | null;
  last_email_from_vendor_at: string | null;
  mentions_count_30d: number;
  mentions_count_90d: number;
}
export interface SubscriptionCancelRec {
  recommend_cancel: boolean;
  reason: string;
  confidence: number;
  dismissed?: boolean;
}
export interface SubscriptionRow {
  id: string;
  vendor_name: string;
  vendor_domain: string | null;
  cadence: string;
  amount_cents: number;
  currency: string;
  last_charge_at: string | null;
  next_charge_at: string | null;
  active: boolean;
  usage_signals: SubscriptionUsageSignals | null;
  cancel_recommendation: SubscriptionCancelRec | null;
  first_seen_at: string;
  last_evaluated_at: string;
  detection_source: string;
}
export interface SubscriptionSummary {
  total_monthly_cents: number;
  count: number;
  top_expensive: SubscriptionRow[];
  top_cancel_candidates: SubscriptionRow[];
}

export interface ForgottenBriefItem {
  id: string;
  thread_kind: string;
  subject: string | null;
  hanging_line: string | null;
  dormancy_days: number;
  prominence_score: number;
  reason: string;
  other_participants: Array<{ name: string; person_node_id: string | null }>;
}

export interface ComposerDraft {
  id: string;
  target_person_id: string | null;
  target_channel: string;
  target_thread_key: string | null;
  subject: string | null;
  body_md: string;
  tone: string;
  status: string;
  source_intent: string | null;
  context_packet_ids: string[];
  draft_seq: number;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  dismissed_at: string | null;
  scope_set: string[];
  model_used: string | null;
  gmail_draft_id: string | null;
  gmail_message_id: string | null;
  gmail_pushed_at: string | null;
  gmail_draft_url: string | null;
}

export interface GmailDraftPushResponse {
  ok: boolean;
  draft: ComposerDraft;
  gmail: {
    account: string;
    draft_id: string;
    message_id: string | null;
    thread_id: string | null;
    draft_url: string;
  };
}

export interface ComposerDraftRequest {
  target_person_id?: string | null;
  target_channel: string;
  intent: string;
  tone?: string;
  prompt?: string | null;
  thread_key?: string | null;
}

export interface ForgottenThread {
  id: string;
  thread_key: string;
  thread_kind: string;
  last_outbound_at: string | null;
  last_inbound_at: string | null;
  dormancy_days: number;
  pending_commitments: Array<{ text: string; by_when?: string | null }>;
  other_participants: Array<{ name: string; person_node_id: string | null }>;
  prominence_score: number;
  reason: string;
  subject: string | null;
  hanging_line: string | null;
  surface_status: string;
  detected_at: string;
  scope_set: string[];
}

export interface MonitorMatch {
  id: string;
  monitor_kind: string | null;
  matched_at: string;
  headline: string | null;
  scope: string | null;
  payload_json: string | null;
}

export interface CaptureRow {
  id: string;
  captured_at: string;
  type: string;
  scope: string;
  text: string;
  processed_at?: string | null;
  meta_json?: string | null;
  ocr_status?: "pending" | "done" | "skipped" | "error" | null;
  ocr_error?: string | null;
  ocr_at?: string | null;
}

export interface ImageCaptureMeta {
  mediaId?: string;
  filename?: string;
  byteSize?: number;
  mime?: string;
  width?: number | null;
  height?: number | null;
  caption?: string | null;
  ocrDegradation?: string | null;
}

export function parseImageMeta(metaJson: string | null | undefined): ImageCaptureMeta | null {
  if (!metaJson) return null;
  try {
    const parsed = JSON.parse(metaJson) as ImageCaptureMeta & Record<string, unknown>;
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.mediaId && !parsed.mime) return null;
    return parsed;
  } catch {
    return null;
  }
}

export interface SearchHit {
  kind: "capture" | "entity";
  id: string;
  rank?: number;
  text?: string;
  name?: string;
  scope?: string;
  capturedAt?: string;
  entityKind?: string;
  snippet?: string;
}

/** A remembered date Memso has woven from any signal: birthdays,
 *  anniversaries, recurring or one-off events. Inferred dates render
 *  with the dotted treatment until the user confirms them. */
export interface DateItem {
  date_id: string;
  kind: string;
  display_name: string;
  description: string | null;
  /** 'YYYY-MM-DD' or '--MM-DD' when the year is unknown. */
  date_value: string;
  /** Always a concrete 'YYYY-MM-DD'. */
  next_occurrence: string;
  days_until: number;
  is_recurring: boolean;
  recurrence_rule: string | null;
  scope: string;
  status: string;
  confidence: number;
  is_inferred: boolean;
  confirmed: boolean;
  person_id: string | null;
  person: {
    id: string;
    display_name: string;
    avatar_url: string | null;
    role: string | null;
    relationship_type: string | null;
  } | null;
  priority_weight: number;
  source_packet_id: string | null;
}

export interface DateNotification {
  notification_id: string;
  title: string;
  body: string | null;
  ref_kind: string;
  ref_id: string;
  created_at: string;
  seen_at: string | null;
  payload: Record<string, unknown> | null;
}

export interface GmailTriageSettings {
  enabled: boolean;
  paused: boolean;
  dry_run: boolean;
  mark_read: boolean;
  live_unlocked: boolean;
}

export interface GmailTriageStatus {
  settings: GmailTriageSettings;
  scope: {
    connected: boolean;
    account: string | null;
    needsReconsent: boolean;
    reconsentUrl: string;
  };
  tally: {
    byMode: Record<"dry_run" | "live", { archive: number; skip: number; guarded: number }>;
    fingerprints: number;
  };
}

export interface GmailTriageRunResult {
  mode: "dry_run" | "live";
  scanned: number;
  fromFingerprint: number;
  fromRegex: number;
  fromLlm: number;
  classifiedPromo: number;
  guarded: number;
  archived: number;
  unsubscribed: number;
  skipped: number;
  errors: number;
}

export interface GmailTriageAction {
  id: string;
  gmail_message_id: string;
  sender_email: string | null;
  sender_domain: string | null;
  subject: string | null;
  classification: string;
  classifier_stage: string;
  confidence: number | null;
  decision: string;
  guard_reason: string | null;
  mode: string;
  executed: number;
  rfc8058: number;
  unsubscribed: number;
  reverted: number;
  error_message: string | null;
  created_at: string;
}

export interface GmailTriageFingerprint {
  id: string;
  sender_domain: string;
  subject_shape: string;
  classification: string;
  source_stage: string;
  hits: number;
  last_seen_at: string;
}

export interface InferenceItem {
  entity_type: string;
  id: string;
  label: string | null;
  inference_reason: string | null;
  inference_source: string | null;
  inference_confidence: number | null;
}

export interface AgenticRun {
  id: string;
  action_kind: string;
  status: string;
  current_step_index: number;
  plan: Array<{ id: string; label: string }>;
  progress: {
    steps?: Array<{ id: string; status: string; summary?: string }>;
    narrative?: string;
    next_prompt?: string | null;
  };
  intermediate_results: Record<string, unknown>;
  payload: Record<string, unknown>;
  started_at: string;
  updated_at: string;
  completed_at: string | null;
  error_message: string | null;
}

export interface InboxCleanupSummary {
  ok: boolean;
  total_gmail_packets: number;
  already_cleaned: number;
  cleaned_by_status: Record<string, number>;
  in_inbox_uncleaned: number;
  by_triage_kind: Record<string, number>;
  by_action: { keep: number; transactions: number; recycle_bin: number };
  archive_candidates: {
    transactions_now: number;
    transactions_label_only: number;
    recycle_bin_now: number;
    total_now: number;
  };
  allowlist_count: number;
}

export interface InboxCleanupCandidate {
  packet_id: string;
  action: "keep" | "transactions" | "recycle_bin";
  label: string | null;
  triage_kind: string;
  sender: string;
  sender_domain: string;
  subject: string;
  captured_at: string;
  archive_eligible_now: boolean;
  archive_after_days: number | null;
  reasons: string[];
}

export interface InboxCleanupCandidatesResponse {
  ok: boolean;
  bucket: string;
  total_in_bucket: number;
  returned: number;
  items: InboxCleanupCandidate[];
}

export interface InboxCleanupApproveResult {
  ok: boolean;
  dry_run?: boolean;
  bucket: string;
  label: string;
  would_archive?: number;
  would_label_only?: number;
  archived?: number;
  labeled?: number;
  errors?: string[];
}

export interface UnsubscribeCandidateView {
  sender_domain: string;
  sender_email: string;
  total_in_window: number;
  unread_count: number;
  has_list_unsubscribe: boolean;
  method: "https_oneclick" | "mailto_draft" | "archive_only";
  triage_kinds: string[];
  reasons: string[];
  message_count: number;
}

export interface InboxCleanupUnsubResult {
  ok: boolean;
  dry_run?: boolean;
  count?: number;
  plan?: Array<{ sender_domain: string; method: string; messages: number }>;
  executed?: number;
  unsubscribed?: number;
  failed?: number;
}

export interface UnsubscribeLogEntry {
  id: string;
  sender_domain: string;
  sender_email: string | null;
  method: string;
  status: string;
  message_count: number;
  unread_count: number;
  detail: string | null;
  unsubscribed_at: string;
  resubscribed_at: string | null;
  resubscription_detected_at: string | null;
}

/** A single Self-profile fact as returned by GET /api/self/profile.
 *  `value` is always the masked form; critical/off_limits raw is only
 *  ever returned through the re-auth-gated reveal endpoint. */
export interface SelfProfileFact {
  id: string;
  fieldKey: string;
  fieldCategory: string;
  value: string;
  tier: "standard" | "critical" | "off_limits";
  status: "inferred" | "confirmed" | "rejected";
  confidence: number;
  extractorKey: string;
  citationCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  revealable: boolean;
}

export interface VaultAuditEntry {
  id: string;
  fieldKey: string | null;
  action: "reveal_granted" | "reveal_denied" | "reauth";
  ip: string | null;
  createdAt: string;
}

export const api = {
  // ---- Self-profile + secure vault (Epic E3) ----
  selfProfile(): Promise<{
    facts: SelfProfileFact[];
    byCategory: Record<string, SelfProfileFact[]>;
  }> {
    return fetch("/api/self/profile", { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ facts: SelfProfileFact[]; byCategory: Record<string, SelfProfileFact[]> }>;
  },
  selfReauth(passphrase: string): Promise<{ ok: boolean; freshForMs?: number }> {
    return fetch("/api/self/reauth", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ passphrase }),
    }).then(json) as Promise<{ ok: boolean; freshForMs?: number }>;
  },
  selfReveal(field: string): Promise<{ ok: boolean; field: string; value: string }> {
    return fetch(`/api/self/reveal/${encodeURIComponent(field)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean; field: string; value: string }>;
  },
  selfVaultAudit(): Promise<{
    ok: boolean;
    entries: VaultAuditEntry[];
    vaultAvailable: boolean;
  }> {
    return fetch("/api/self/vault/audit", { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ ok: boolean; entries: VaultAuditEntry[]; vaultAvailable: boolean }>;
  },
  // ---- Agentic actions (Phase 3) ----
  agenticKinds(): Promise<{ ok: boolean; kinds: string[] }> {
    return fetch("/api/agentic/kinds", { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ ok: boolean; kinds: string[] }>;
  },
  agenticStart(
    kind: string,
    payload: Record<string, unknown> = {},
  ): Promise<{ ok: boolean; run_id: string; run: AgenticRun }> {
    return fetch("/api/agentic/start", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ kind, payload }),
    }).then(json) as Promise<{ ok: boolean; run_id: string; run: AgenticRun }>;
  },
  agenticRuns(status?: string): Promise<{ ok: boolean; runs: AgenticRun[] }> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return fetch(`/api/agentic/runs${qs}`, { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ ok: boolean; runs: AgenticRun[] }>;
  },
  agenticGet(id: string): Promise<{ ok: boolean; run: AgenticRun }> {
    return fetch(`/api/agentic/${encodeURIComponent(id)}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; run: AgenticRun }>;
  },
  agenticResume(
    id: string,
    input?: string,
  ): Promise<{ ok: boolean; run: AgenticRun }> {
    return fetch(`/api/agentic/${encodeURIComponent(id)}/resume`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(input != null ? { input } : {}),
    }).then(json) as Promise<{ ok: boolean; run: AgenticRun }>;
  },
  agenticPause(id: string): Promise<{ ok: boolean; run: AgenticRun }> {
    return fetch(`/api/agentic/${encodeURIComponent(id)}/pause`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean; run: AgenticRun }>;
  },
  agenticCancel(id: string): Promise<{ ok: boolean; run: AgenticRun }> {
    return fetch(`/api/agentic/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean; run: AgenticRun }>;
  },
  // ---- In-app teaching microcopy ----
  teachingDismissed(): Promise<{ ok: boolean; dismissed: string[] }> {
    return fetch("/api/teaching/dismissed", {
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; dismissed: string[] }>;
  },
  teachingDismiss(id: string): Promise<{ ok: boolean; dismissed: string[] }> {
    return fetch(`/api/teaching/${encodeURIComponent(id)}/dismiss`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean; dismissed: string[] }>;
  },
  teachingRestore(id: string): Promise<{ ok: boolean; dismissed: string[] }> {
    return fetch(`/api/teaching/${encodeURIComponent(id)}/restore`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean; dismissed: string[] }>;
  },
  teachingRestoreAll(): Promise<{ ok: boolean; dismissed: string[] }> {
    return fetch("/api/teaching/restore-all", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean; dismissed: string[] }>;
  },
  health(): Promise<{ ok: boolean; ts: string }> {
    return fetch("/api/health", { credentials: "same-origin" }).then(json) as Promise<{ ok: boolean; ts: string }>;
  },
  version(): Promise<VersionPayload> {
    return fetch("/api/version", { credentials: "same-origin", cache: "no-store" }).then(json) as Promise<VersionPayload>;
  },
  dashboardToday(): Promise<DashboardToday> {
    // Boot-blocking: /u/<handle>/today renders this fetch as its
    // landing canvas. Same rationale as engagementStream() above.
    return fetch("/api/dashboard/today", withFetchTimeout({ credentials: "same-origin" })).then(json) as Promise<DashboardToday>;
  },
  forgotten(status = "pending", limit = 20): Promise<{ ok: boolean; threads: ForgottenThread[]; total: number }> {
    return fetch(
      `/api/forgotten?status=${encodeURIComponent(status)}&limit=${limit}`,
      { credentials: "same-origin" },
    ).then(json) as Promise<{ ok: boolean; threads: ForgottenThread[]; total: number }>;
  },
  forgottenDismiss(id: string): Promise<{ ok: boolean }> {
    return fetch(`/api/forgotten/${encodeURIComponent(id)}/dismiss`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  forgottenResolve(id: string): Promise<{ ok: boolean }> {
    return fetch(`/api/forgotten/${encodeURIComponent(id)}/resolve`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  // ---- Composer Agent ----
  composerDraft(req: ComposerDraftRequest): Promise<{ ok: boolean; draft: ComposerDraft }> {
    return fetch("/api/composer/draft", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(req),
    }).then(json) as Promise<{ ok: boolean; draft: ComposerDraft }>;
  },
  composerRegenerate(
    id: string,
    body: { feedback?: string | null; tone?: string },
  ): Promise<{ ok: boolean; draft: ComposerDraft }> {
    return fetch(`/api/composer/${encodeURIComponent(id)}/regenerate`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(body),
    }).then(json) as Promise<{ ok: boolean; draft: ComposerDraft }>;
  },
  composerFinalize(id: string): Promise<{ ok: boolean; draft: ComposerDraft }> {
    return fetch(`/api/composer/${encodeURIComponent(id)}/finalize`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean; draft: ComposerDraft }>;
  },
  composerDismiss(id: string): Promise<{ ok: boolean; draft: ComposerDraft }> {
    return fetch(`/api/composer/${encodeURIComponent(id)}/dismiss`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean; draft: ComposerDraft }>;
  },
  composerPushToGmail(
    id: string,
    body: { to?: string | null } = {},
  ): Promise<GmailDraftPushResponse> {
    return fetch(`/api/composer/${encodeURIComponent(id)}/push-to-gmail`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(body ?? {}),
    }).then(json) as Promise<GmailDraftPushResponse>;
  },
  // ---- Inferred-vs-Confirmed universal pattern ----
  inferenceNeedsReview(
    entityType?: string,
    limit = 20,
  ): Promise<{ ok: boolean; items: InferenceItem[] }> {
    const qp = new URLSearchParams();
    if (entityType) qp.set("entity_type", entityType);
    qp.set("limit", String(limit));
    return fetch(`/api/inference/needs-review?${qp.toString()}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; items: InferenceItem[] }>;
  },
  inferenceNeedsReviewCounts(): Promise<{
    ok: boolean;
    counts: Record<string, number>;
    high_confidence_total: number;
  }> {
    return fetch("/api/inference/needs-review-counts", {
      credentials: "same-origin",
    }).then(json) as Promise<{
      ok: boolean;
      counts: Record<string, number>;
      high_confidence_total: number;
    }>;
  },
  inferenceConfirm(entityType: string, id: string): Promise<{ ok: boolean }> {
    return fetch(
      `/api/inference/${encodeURIComponent(entityType)}/${encodeURIComponent(id)}/confirm`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        credentials: "same-origin",
        body: "{}",
      },
    ).then(json) as Promise<{ ok: boolean }>;
  },
  inferenceReject(entityType: string, id: string): Promise<{ ok: boolean }> {
    return fetch(
      `/api/inference/${encodeURIComponent(entityType)}/${encodeURIComponent(id)}/reject`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        credentials: "same-origin",
        body: "{}",
      },
    ).then(json) as Promise<{ ok: boolean }>;
  },
  inferencePatch(
    entityType: string,
    id: string,
    body: Record<string, unknown>,
  ): Promise<{ ok: boolean }> {
    return fetch(
      `/api/inference/${encodeURIComponent(entityType)}/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        credentials: "same-origin",
        body: JSON.stringify(body),
      },
    ).then(json) as Promise<{ ok: boolean }>;
  },
  composerList(status = "active"): Promise<{ ok: boolean; drafts: ComposerDraft[] }> {
    const qs = status && status !== "active" ? `?status=${encodeURIComponent(status)}` : "";
    return fetch(`/api/composer/drafts${qs}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; drafts: ComposerDraft[] }>;
  },
  composerGet(id: string): Promise<{ ok: boolean; draft: ComposerDraft }> {
    return fetch(`/api/composer/${encodeURIComponent(id)}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; draft: ComposerDraft }>;
  },
  // ---- Track C: context-aware surfacing ----
  contextCapture(body: {
    source?: string;
    location_lat?: number | null;
    location_lng?: number | null;
    location_accuracy?: number | null;
    device_state?: string;
    local_hour?: number;
  }): Promise<{ ok: boolean; context_id: string; resolved: string }> {
    return fetch("/api/context/capture", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(body),
    }).then(json) as Promise<{ ok: boolean; context_id: string; resolved: string }>;
  },
  contextSettings(): Promise<{
    ok: boolean;
    prefs: ContextPrefs;
    places: NamedPlace[];
    supported_requirements: string[];
  }> {
    return fetch("/api/context/settings", { credentials: "same-origin" }).then(json) as Promise<{
      ok: boolean;
      prefs: ContextPrefs;
      places: NamedPlace[];
      supported_requirements: string[];
    }>;
  },
  contextSetPrefs(prefs: Partial<ContextPrefs>): Promise<{ ok: boolean; prefs: ContextPrefs }> {
    return fetch("/api/context/settings", {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(prefs),
    }).then(json) as Promise<{ ok: boolean; prefs: ContextPrefs }>;
  },
  contextAddPlace(p: {
    name: string;
    lat: number;
    lng: number;
    kind: string;
    radius_m?: number;
  }): Promise<{ ok: boolean; place_id: string }> {
    return fetch("/api/context/places", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(p),
    }).then(json) as Promise<{ ok: boolean; place_id: string }>;
  },
  contextConfirmPlace(
    id: string,
    patch: { name?: string; kind?: string } = {},
  ): Promise<{ ok: boolean }> {
    return fetch(`/api/context/places/${encodeURIComponent(id)}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(patch),
    }).then(json) as Promise<{ ok: boolean }>;
  },
  contextDeletePlace(id: string): Promise<{ ok: boolean }> {
    return fetch(`/api/context/places/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  commitments(opts: { status?: CommitmentStatus; person_id?: string } = {}): Promise<{ ok: boolean; commitments: Commitment[] }> {
    const q = new URLSearchParams();
    if (opts.status) q.set("status", opts.status);
    if (opts.person_id) q.set("person_id", opts.person_id);
    const qs = q.toString();
    return fetch(`/api/commitments${qs ? `?${qs}` : ""}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; commitments: Commitment[] }>;
  },
  commitmentTrends(): Promise<{ ok: boolean; trends: CommitmentTrends }> {
    return fetch("/api/commitments/trends", {
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; trends: CommitmentTrends }>;
  },
  setCommitmentStatus(id: string, status: CommitmentStatus): Promise<{ ok: boolean; commitment: Commitment }> {
    return fetch(`/api/commitments/${encodeURIComponent(id)}/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ status }),
    }).then(json) as Promise<{ ok: boolean; commitment: Commitment }>;
  },
  goals(): Promise<{ ok: boolean; goals: Goal[] }> {
    return fetch("/api/goals", { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ ok: boolean; goals: Goal[] }>;
  },
  createGoal(input: { title: string; target_date?: string | null; scope_set?: string[] }): Promise<{ ok: boolean; goal: Goal }> {
    return fetch("/api/goals", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(input),
    }).then(json) as Promise<{ ok: boolean; goal: Goal }>;
  },
  conversationsRecent(limit = 20): Promise<{ ok: boolean; summaries: ThreadSummary[] }> {
    return fetch(`/api/conversations/recent?limit=${encodeURIComponent(String(limit))}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; summaries: ThreadSummary[] }>;
  },
  conversationSummary(threadKey: string): Promise<{ ok: boolean; summary: ThreadSummary }> {
    return fetch(
      `/api/conversations/${encodeURIComponent(threadKey)}/summary`,
      { credentials: "same-origin" },
    ).then(json) as Promise<{ ok: boolean; summary: ThreadSummary }>;
  },
  adminResetUserData(dryRun: boolean): Promise<AdminResetResult> {
    return fetch("/api/settings/admin/reset-user-data", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ dry_run: dryRun }),
    }).then(json) as Promise<AdminResetResult>;
  },
  adminListSnapshots(): Promise<{ ok: boolean; snapshots: AdminSnapshot[] }> {
    return fetch("/api/settings/admin/snapshots", {
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; snapshots: AdminSnapshot[] }>;
  },
  adminRestoreSnapshot(
    snapshotPath: string,
  ): Promise<{ ok: boolean; restored_from: string; safety_snapshot: string; note: string }> {
    return fetch("/api/settings/admin/restore-snapshot", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ snapshot_path: snapshotPath }),
    }).then(json) as Promise<{
      ok: boolean;
      restored_from: string;
      safety_snapshot: string;
      note: string;
    }>;
  },
  adminDeleteAccount(): Promise<{
    ok: boolean;
    snapshot_path: string | null;
    deleted_user_id: string;
    tables_cleared: string[];
    error?: string;
  }> {
    return fetch("/api/settings/admin/delete-account", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ confirm: "DELETE_MY_ACCOUNT" }),
    }).then(json) as Promise<{
      ok: boolean;
      snapshot_path: string | null;
      deleted_user_id: string;
      tables_cleared: string[];
      error?: string;
    }>;
  },
  adminRestoreAccountSnapshot(
    snapshotPath: string,
  ): Promise<{ ok: boolean; restored_from: string; safety_snapshot: string; note: string }> {
    return fetch("/api/settings/admin/restore-account-snapshot", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ snapshot_path: snapshotPath }),
    }).then(json) as Promise<{
      ok: boolean;
      restored_from: string;
      safety_snapshot: string;
      note: string;
    }>;
  },
  contextInferPlaces(): Promise<{ ok: boolean; inferred: NamedPlace[] }> {
    return fetch("/api/context/places/infer", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean; inferred: NamedPlace[] }>;
  },
  contextPending(): Promise<{ ok: boolean; notifications: PendingNotification[] }> {
    return fetch("/api/context/pending", { credentials: "same-origin" }).then(json) as Promise<{
      ok: boolean;
      notifications: PendingNotification[];
    }>;
  },
  contextDismiss(id: string): Promise<{ ok: boolean }> {
    return fetch(`/api/context/pending/${encodeURIComponent(id)}/dismiss`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  capture(text: string, type: string, scope: string): Promise<{ id: string; ts: string; type: string; scope: string }> {
    return fetch("/api/capture", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ text, type, scope }),
    }).then(json) as Promise<{ id: string; ts: string; type: string; scope: string }>;
  },
  reprocessOcr(captureId: string): Promise<{ id: string; ocr: { status: "done" | "skipped" | "error"; textChars: number; degradation: string | null } }> {
    return fetch(`/api/captures/${encodeURIComponent(captureId)}/reprocess-ocr`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ id: string; ocr: { status: "done" | "skipped" | "error"; textChars: number; degradation: string | null } }>;
  },
  aboutYou(): Promise<{ facets: AboutYouFacet[] }> {
    return fetch("/api/about-you", { credentials: "same-origin" }).then(json) as Promise<{ facets: AboutYouFacet[] }>;
  },
  aboutYouFacet(facet: string, limit = 100): Promise<{ facet: string; table?: string; rows: Record<string, unknown>[]; note?: string }> {
    return fetch(`/api/about-you/facet/${encodeURIComponent(facet)}?limit=${limit}`, { credentials: "same-origin" }).then(json) as Promise<{ facet: string; table?: string; rows: Record<string, unknown>[]; note?: string }>;
  },
  aboutYouFeedback(facet: string, verdict: "confirmed" | "rejected" | "corrected", note?: string): Promise<{ ok: boolean }> {
    return fetch("/api/about-you/feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ facet, verdict, note }),
    }).then(json) as Promise<{ ok: boolean }>;
  },
  recentCaptures(limit = 20): Promise<{ captures: CaptureRow[] }> {
    return fetch(`/api/captures/recent?limit=${limit}`, { credentials: "same-origin" }).then(json) as Promise<{ captures: CaptureRow[] }>;
  },
  capture_get(id: string): Promise<CaptureRow> {
    return fetch(`/api/captures/${encodeURIComponent(id)}`, { credentials: "same-origin" }).then(json) as Promise<CaptureRow>;
  },
  capture_job(id: string): Promise<{ id: string; status: string; enqueued_at: string; started_at: string | null; finished_at: string | null; error: string | null; attempts: number }> {
    return fetch(`/api/captures/${encodeURIComponent(id)}/job`, { credentials: "same-origin" }).then(json) as Promise<{ id: string; status: string; enqueued_at: string; started_at: string | null; finished_at: string | null; error: string | null; attempts: number }>;
  },
  search(q: string, limit = 20): Promise<{ q: string; hits: SearchHit[] }> {
    return fetch(`/api/search?q=${encodeURIComponent(q)}&limit=${limit}`, { credentials: "same-origin" }).then(json) as Promise<{ q: string; hits: SearchHit[] }>;
  },
  engagements(opts: { status?: "active" | "completed" | "abandoned" | "postponed" | "all"; kind?: string; limit?: number } = {}): Promise<{
    engagements: Array<{
      id: string;
      kind: string;
      title: string;
      description: string | null;
      status: string;
      scope: string | null;
      due_at: string | null;
      related_people_json: string | null;
      detection_source: string;
      detection_reason: string | null;
      last_activity_at: string | null;
      activity_count: number;
      created_at: string;
      updated_at: string;
    }>;
  }> {
    const params = new URLSearchParams();
    if (opts.status) params.set("status", opts.status);
    if (opts.kind) params.set("kind", opts.kind);
    if (opts.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return fetch(`/api/engagements${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(json) as Promise<{
      engagements: Array<{
        id: string;
        kind: string;
        title: string;
        description: string | null;
        status: string;
        scope: string | null;
        due_at: string | null;
        related_people_json: string | null;
        detection_source: string;
        detection_reason: string | null;
        last_activity_at: string | null;
        activity_count: number;
        created_at: string;
        updated_at: string;
      }>;
    }>;
  },
  engagement(id: string): Promise<EngagementDetailResponse> {
    return fetch(`/api/engagements/${encodeURIComponent(id)}`, { credentials: "same-origin" }).then(json) as Promise<EngagementDetailResponse>;
  },
  engagementStatus(id: string, status: "active" | "completed" | "abandoned" | "postponed"): Promise<{ engagement: unknown }> {
    return fetch(`/api/engagements/${encodeURIComponent(id)}/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ status }),
    }).then(json) as Promise<{ engagement: unknown }>;
  },
  engagementAddActivity(id: string, summary: string): Promise<{ activity: unknown }> {
    return fetch(`/api/engagements/${encodeURIComponent(id)}/activity`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ summary }),
    }).then(json) as Promise<{ activity: unknown }>;
  },
  engagementStream(): Promise<EngagementStreamResponse> {
    // Boot-blocking: EngagementStream renders this fetch's result as
    // the user's landing canvas (/u/<handle>). Without the timeout a
    // hung request leaves the page on "Loading..." indefinitely. See
    // withFetchTimeout() above for the rationale.
    return fetch(`/api/engagements/stream`, withFetchTimeout({ credentials: "same-origin" })).then(json) as Promise<EngagementStreamResponse>;
  },
  engagementsDetect(): Promise<{ ok: boolean; scanned: number; engagements: number; activitiesAdded: number }> {
    return fetch(`/api/engagements/detect`, {
      method: "POST",
      headers: csrfHeaders(),
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; scanned: number; engagements: number; activitiesAdded: number }>;
  },
  briefItems(opts: { status?: "open" | "in_progress" | "done" | "dismissed" | "snoozed" | "all"; limit?: number } = {}): Promise<{
    items: Array<{
      id: string;
      brief_id: string | null;
      kind: string;
      title: string;
      body: string | null;
      source_ref_json: string | null;
      status: string;
      priority: number;
      due_at: string | null;
      scope: string | null;
      created_at: string;
      updated_at: string;
      decided_at: string | null;
      snoozed_until: string | null;
    }>;
    /** Open engagements with a live actionable signal beyond the
     *  returned page. The SPA renders "N more in the engagement
     *  stream →" when this is positive. */
    more_count?: number;
  }> {
    const params = new URLSearchParams();
    if (opts.status) params.set("status", opts.status);
    if (opts.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return fetch(`/api/brief/items${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(json) as Promise<{
      items: Array<{
        id: string;
        brief_id: string | null;
        kind: string;
        title: string;
        body: string | null;
        source_ref_json: string | null;
        status: string;
        priority: number;
        due_at: string | null;
        scope: string | null;
        created_at: string;
        updated_at: string;
        decided_at: string | null;
        snoozed_until: string | null;
      }>;
      more_count?: number;
    }>;
  },
  updateBriefItemStatus(id: string, status: "open" | "in_progress" | "done" | "dismissed" | "snoozed", opts: { snoozed_until?: string } = {}): Promise<{ ok: boolean; item: unknown }> {
    return fetch(`/api/brief/items/${encodeURIComponent(id)}/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ status, ...(opts.snoozed_until ? { snoozed_until: opts.snoozed_until } : {}) }),
    }).then(json) as Promise<{ ok: boolean; item: unknown }>;
  },
  briefRunNow(): Promise<{ ok: boolean; date: string; brief?: { headline: string | null; body: string | null } }> {
    return fetch(`/api/brief/run-now`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; date: string; brief?: { headline: string | null; body: string | null } }>;
  },
  agents(): Promise<{ agents: AgentSummary[] }> {
    return fetch(`/api/agents`, { credentials: "same-origin" }).then(json) as Promise<{ agents: AgentSummary[] }>;
  },
  /** W033 Q17: record a correction to an inferred field so the named
   *  extractor steers away from the mistake on its next LLM call. */
  extractorCorrect(input: {
    extractor_key: string;
    field?: string;
    original_value?: string | null;
    corrected_value: string;
    packet_id?: string | null;
    context?: Record<string, unknown>;
  }): Promise<{ ok: boolean; id?: string }> {
    return fetch(`/api/extractor/correct`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(input),
    }).then(json) as Promise<{ ok: boolean; id?: string }>;
  },
  brainstormTurn(args: { sessionId?: string; text: string; scope?: string }): Promise<BrainstormTurnResponse> {
    return fetch(`/api/brainstorm/turn`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(args),
    }).then(json) as Promise<BrainstormTurnResponse>;
  },
  brainstormSessions(): Promise<{ sessions: BrainstormSessionSummary[] }> {
    return fetch(`/api/brainstorm/sessions`, { credentials: "same-origin" }).then(json) as Promise<{ sessions: BrainstormSessionSummary[] }>;
  },
  graph(args: { scope?: string; kind?: string; limit?: number } = {}): Promise<GraphResponse> {
    const params = new URLSearchParams();
    if (args.scope) params.set("scope", args.scope);
    if (args.kind) params.set("kind", args.kind);
    if (args.limit) params.set("limit", String(args.limit));
    return fetch(`/api/graph?${params.toString()}`, { credentials: "same-origin" }).then(json) as Promise<GraphResponse>;
  },
  graphByKind(): Promise<{ kinds: { kind: string; n: number }[] }> {
    return fetch(`/api/graph/by-kind`, { credentials: "same-origin" }).then(json) as Promise<{ kinds: { kind: string; n: number }[] }>;
  },
  synthesisStatus(): Promise<{
    last_run_at: string | null;
    packet_count_seen: number | null;
    total_active_insights: number;
    insight_counts_by_kind: Record<string, number>;
  }> {
    return fetch(`/api/synthesis/status`, { credentials: "same-origin" }).then(json) as Promise<{
      last_run_at: string | null;
      packet_count_seen: number | null;
      total_active_insights: number;
      insight_counts_by_kind: Record<string, number>;
    }>;
  },
  recommendations(
    status = "pending",
    opts: { sort?: "top" | "recent"; limit?: number } = {},
  ): Promise<{ recommendations: Recommendation[] }> {
    const params = new URLSearchParams({ status });
    if (opts.sort) params.set("sort", opts.sort);
    if (opts.limit) params.set("limit", String(opts.limit));
    return fetch(`/api/recommendations?${params.toString()}`, { credentials: "same-origin" }).then(json) as Promise<{ recommendations: Recommendation[] }>;
  },
  actions(opts: { source?: "recommendation" | "task" | "observation"; includeDone?: boolean; limit?: number } = {}): Promise<{
    actions: Array<{
      id: string;
      source: "recommendation" | "task" | "observation";
      kind: string;
      title: string;
      body: string | null;
      status: string;
      scope: string | null;
      due_at: string | null;
      created_at: string;
      meta: Record<string, unknown>;
    }>;
    counts: { recommendation: number; task: number; observation: number; total: number };
  }> {
    const params = new URLSearchParams();
    if (opts.source) params.set("source", opts.source);
    if (opts.includeDone) params.set("include_done", "1");
    if (opts.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return fetch(`/api/actions${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(json) as Promise<{
      actions: Array<{
        id: string;
        source: "recommendation" | "task" | "observation";
        kind: string;
        title: string;
        body: string | null;
        status: string;
        scope: string | null;
        due_at: string | null;
        created_at: string;
        meta: Record<string, unknown>;
      }>;
      counts: { recommendation: number; task: number; observation: number; total: number };
    }>;
  },
  /** The "things waiting on you" queue: open tasks + pending
   *  recommendations + actionable observations, unioned directly
   *  from the base tables, ranked by due-soon → Wave 1A direction →
   *  recency, capped server-side (default top 25). */
  actionsQueue(opts: { source?: "recommendation" | "task" | "observation"; limit?: number } = {}): Promise<{
    actions: Array<{
      id: string;
      source: "recommendation" | "task" | "observation";
      kind: string;
      title: string;
      body: string | null;
      status: string;
      scope: string | null;
      due_at: string | null;
      created_at: string;
      meta: Record<string, unknown>;
    }>;
    counts: { recommendation: number; task: number; observation: number; total: number };
  }> {
    const params = new URLSearchParams();
    if (opts.source) params.set("source", opts.source);
    if (opts.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return fetch(`/api/actions/queue${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(json) as Promise<{
      actions: Array<{
        id: string;
        source: "recommendation" | "task" | "observation";
        kind: string;
        title: string;
        body: string | null;
        status: string;
        scope: string | null;
        due_at: string | null;
        created_at: string;
        meta: Record<string, unknown>;
      }>;
      counts: { recommendation: number; task: number; observation: number; total: number };
    }>;
  },
  patchTask(id: string, patch: { status?: "open" | "done" | "dropped" | "snoozed"; title?: string; due_at?: string | null }): Promise<{ task: unknown }> {
    return fetch(`/api/tasks/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(patch),
    }).then(json) as Promise<{ task: unknown }>;
  },
  decideRecommendation(id: string, status: "accepted" | "dismissed" | "snoozed"): Promise<{ ok: boolean; dispatched?: boolean; trust_message?: string | null; result?: { handled: boolean; via: string | null; text: string | null; model: string | null; route: string | null; degradation: string | null } }> {
    return fetch(`/api/recommendations/${encodeURIComponent(id)}/decision`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ status }),
    }).then(json) as Promise<{ ok: boolean; dispatched?: boolean; trust_message?: string | null; result?: { handled: boolean; via: string | null; text: string | null; model: string | null; route: string | null; degradation: string | null } }>;
  },
  recommendationsList(opts: { status?: string; surface?: string; limit?: number } = {}): Promise<{ recommendations: Recommendation[] }> {
    const params = new URLSearchParams();
    params.set("status", opts.status ?? "pending");
    if (opts.surface) params.set("surface", opts.surface);
    if (opts.limit) params.set("limit", String(opts.limit));
    return fetch(`/api/recommendations?${params.toString()}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ recommendations: Recommendation[] }>;
  },
  acceptRecommendation(id: string): Promise<{ ok: boolean; result: ActionExecutionResult }> {
    return fetch(`/api/recommendations/${encodeURIComponent(id)}/accept`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; result: ActionExecutionResult }>;
  },
  rejectRecommendation(id: string): Promise<{ ok: boolean }> {
    return fetch(`/api/recommendations/${encodeURIComponent(id)}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  // W011 hide-sender-forever. The backend resolves the sender from
  // whichever id is supplied (recommendation or engagement).
  hideSenderForever(input: {
    recommendation_id?: string;
    engagement_id?: string;
    observation_id?: string;
    sender?: string;
    surface?: string;
    item_kind?: string;
    item_id?: string;
    label?: string;
  }): Promise<{ ok: boolean; sender?: string; trust_message?: string; error?: string }> {
    return fetch(`/api/suppressions/hide-forever`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(input),
    }).then(json) as Promise<{ ok: boolean; sender?: string; trust_message?: string; error?: string }>;
  },
  curationScorecard(): Promise<{ ok: boolean; surfaces: CurationSurfaceScore[]; computed_at: string | null }> {
    return fetch(`/api/admin/curation/scorecard`, { credentials: "same-origin" }).then(json) as Promise<{
      ok: boolean;
      surfaces: CurationSurfaceScore[];
      computed_at: string | null;
    }>;
  },
  recomputeCurationScorecard(): Promise<{ ok: boolean; surfaces: CurationSurfaceScore[]; computed_at: string }> {
    return fetch(`/api/admin/curation/scorecard/recompute`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; surfaces: CurationSurfaceScore[]; computed_at: string }>;
  },
  listSuppressions(): Promise<{ suppressions: SuppressedSender[] }> {
    return fetch(`/api/suppressions`, { credentials: "same-origin" }).then(json) as Promise<{
      suppressions: SuppressedSender[];
    }>;
  },
  removeSuppression(id: string): Promise<{ ok: boolean }> {
    return fetch(`/api/suppressions/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  editRecommendation(id: string, payloadPatch: Record<string, unknown>): Promise<{ ok: boolean; payload: Record<string, unknown> }> {
    return fetch(`/api/recommendations/${encodeURIComponent(id)}/edit`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ payload_patch: payloadPatch }),
    }).then(json) as Promise<{ ok: boolean; payload: Record<string, unknown> }>;
  },
  recommendationAudit(id: string): Promise<{ ok: boolean; audit: ActionAuditEntry[] }> {
    return fetch(`/api/recommendations/${encodeURIComponent(id)}/audit`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; audit: ActionAuditEntry[] }>;
  },
  observations(opts: { status?: ObservationStatus; includeTest?: boolean; limit?: number } = {}): Promise<{ observations: Observation[] }> {
    const params = new URLSearchParams();
    if (opts.status) params.set("status", opts.status);
    if (opts.includeTest) params.set("include_test", "1");
    if (opts.limit) params.set("limit", String(opts.limit));
    return fetch(`/api/observations?${params.toString()}`, { credentials: "same-origin" }).then(json) as Promise<{ observations: Observation[] }>;
  },
  acknowledgeObservation(id: string): Promise<{ observation: Observation }> {
    return fetch(`/api/observations/${encodeURIComponent(id)}/seen`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ observation: Observation }>;
  },
  acceptObservationAction(
    id: string,
    confirmBulk = false,
  ): Promise<{
    ok: boolean;
    no_action?: boolean;
    recommendation_id?: string;
    result?: {
      success: boolean;
      summary: string;
      details_json?: {
        requires_bulk_confirmation?: boolean;
        item_count?: number;
        [k: string]: unknown;
      } | null;
    };
  }> {
    return fetch(`/api/observations/${encodeURIComponent(id)}/accept`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ confirm_bulk: confirmBulk }),
    }).then(json) as Promise<{
      ok: boolean;
      no_action?: boolean;
      recommendation_id?: string;
      result?: {
        success: boolean;
        summary: string;
        details_json?: {
          requires_bulk_confirmation?: boolean;
          item_count?: number;
          [k: string]: unknown;
        } | null;
      };
    }>;
  },
  archiveObservation(id: string): Promise<{ observation: Observation }> {
    return fetch(`/api/observations/${encodeURIComponent(id)}/archive`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ observation: Observation }>;
  },
  falsePositiveObservation(id: string): Promise<{ observation: Observation }> {
    return fetch(`/api/observations/${encodeURIComponent(id)}/false-positive`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ observation: Observation }>;
  },
  observationPreferences(): Promise<{ preferences: ObservationPreference[] }> {
    return fetch(`/api/observations/preferences`, { credentials: "same-origin" }).then(json) as Promise<{ preferences: ObservationPreference[] }>;
  },
  privateReview(): Promise<{ gated: boolean; items: PrivateRevision[]; unreviewed: number }> {
    return fetch(`/api/private-review`, { credentials: "same-origin" }).then(json) as Promise<{ gated: boolean; items: PrivateRevision[]; unreviewed: number }>;
  },
  privateReviewKeep(packetId: string): Promise<{ ok: boolean }> {
    return fetch(`/api/private-review/${encodeURIComponent(packetId)}/keep`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  privateReviewReclassify(packetId: string, scope: string): Promise<{ ok: boolean }> {
    return fetch(`/api/private-review/${encodeURIComponent(packetId)}/reclassify`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ scope }),
    }).then(json) as Promise<{ ok: boolean }>;
  },
  privateReviewMarkAllReviewed(): Promise<{ ok: boolean; reviewed: number }> {
    return fetch(`/api/private-review/mark-all-reviewed`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; reviewed: number }>;
  },
  setObservationPreference(kind: ObservationKind, muted: boolean): Promise<{ preference: ObservationPreference }> {
    return fetch(`/api/observations/preferences`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ kind, muted }),
    }).then(json) as Promise<{ preference: ObservationPreference }>;
  },
  composerRun(args: {
    kind: "email_draft" | "reply_draft" | "meeting_invite" | "vault_note" | "summary";
    payload: Record<string, unknown>;
    context_packet_ids?: string[];
    opus_escalate?: boolean;
  }): Promise<ComposerRunResponse> {
    return fetch(`/api/agents/composer/run`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({
        kind: args.kind,
        payload: args.payload,
        context_packet_ids: args.context_packet_ids ?? [],
        opus_escalate: !!args.opus_escalate,
      }),
    }).then(json) as Promise<ComposerRunResponse>;
  },
  connectors(): Promise<ConnectorsResponse> {
    return fetch(`/api/connectors`, { credentials: "same-origin" }).then(json) as Promise<ConnectorsResponse>;
  },
  connectorOp(
    id: string,
    op: "pause" | "resume" | "refresh" | "backfill" | "disconnect",
  ): Promise<{ ok: boolean; error?: string }> {
    return fetch(`/api/connectors/${encodeURIComponent(id)}/${op}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; error?: string }>;
  },
  connectorUpdateSettings(
    id: string,
    patch: { privacy_label?: string | null; scope?: string; config?: Record<string, unknown> },
  ): Promise<{ ok: boolean; error?: string }> {
    return fetch(`/api/connectors/${encodeURIComponent(id)}/settings`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(patch),
    }).then(json) as Promise<{ ok: boolean; error?: string }>;
  },
  connectorDisconnect(
    id: string,
  ): Promise<{ ok: boolean; action?: string; error?: string }> {
    return fetch(`/api/connectors/${encodeURIComponent(id)}/disconnect`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ confirm: "DISCONNECT" }),
    }).then(json) as Promise<{
      ok: boolean;
      action?: string;
      error?: string;
    }>;
  },
  connectorPurgeEstimate(id: string): Promise<{
    ok: boolean;
    packets: number;
    engagements: number;
    observations: number;
  }> {
    return fetch(
      `/api/connectors/${encodeURIComponent(id)}/purge-estimate`,
      { credentials: "same-origin" },
    ).then(json) as Promise<{
      ok: boolean;
      packets: number;
      engagements: number;
      observations: number;
    }>;
  },
  connectorDelete(
    id: string,
    deleteData: boolean,
  ): Promise<{
    ok: boolean;
    action?: string;
    data_kept?: boolean;
    purged?: {
      packets: number;
      engagements: number;
      observations: number;
    } | null;
    error?: string;
  }> {
    return fetch(`/api/connectors/${encodeURIComponent(id)}/delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ confirm: "DELETE", delete_data: deleteData }),
    }).then(json) as Promise<{
      ok: boolean;
      action?: string;
      data_kept?: boolean;
      purged?: {
        packets: number;
        engagements: number;
        observations: number;
      } | null;
      error?: string;
    }>;
  },
  connectorReconnect(id: string): Promise<{ ok: boolean; reconnectUrl: string | null }> {
    return fetch(`/api/connectors/${encodeURIComponent(id)}/reconnect`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; reconnectUrl: string | null }>;
  },
  connectorHealth(id: string): Promise<{
    ok: boolean;
    health: "green" | "yellow" | "red";
    enabled: boolean;
    last_status: string | null;
    last_sync_at: string | null;
    error: string | null;
    suggested_action: string;
  }> {
    return fetch(`/api/connectors/${encodeURIComponent(id)}/health`, {
      credentials: "same-origin",
    }).then(json) as Promise<{
      ok: boolean;
      health: "green" | "yellow" | "red";
      enabled: boolean;
      last_status: string | null;
      last_sync_at: string | null;
      error: string | null;
      suggested_action: string;
    }>;
  },
  workConnectorsHealth(): Promise<{ connectors: WorkConnectorHealth[] }> {
    return fetch(`/api/work-connectors/health`, { credentials: "same-origin" }).then(json) as Promise<{
      connectors: WorkConnectorHealth[];
    }>;
  },
  workConnectorsBridgeHealth(): Promise<{
    ok: boolean;
    cached: boolean;
    bridges: Array<{
      kind: string;
      label: string;
      ok: boolean;
      last_checked: string;
      last_seen_at: string | null;
      version?: string | null;
      url?: string | null;
      expected: boolean;
      error?: string | null;
      uptime_seconds?: number | null;
      handle_count?: number | null;
      handle_health?: "ok" | "warn" | "crit" | null;
    }>;
  }> {
    return fetch(`/api/work-connectors/bridges/health`, {
      credentials: "same-origin",
    }).then(json) as Promise<{
      ok: boolean;
      cached: boolean;
      bridges: Array<{
        kind: string;
        label: string;
        ok: boolean;
        last_checked: string;
        last_seen_at: string | null;
        version?: string | null;
        url?: string | null;
        expected: boolean;
        error?: string | null;
      }>;
    }>;
  },
  workConnectorsRecycleCom(): Promise<{
    ok: boolean;
    error?: string;
    recycle?: {
      ok: boolean;
      before?: { uptime_seconds: number; handle_count: number };
      after?: { uptime_seconds: number; handle_count: number };
      recycled_at?: string;
    };
  }> {
    return fetch(`/api/work-connectors/outlook-bridge/recycle-com`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      body: "{}",
    }).then(json) as Promise<{ ok: boolean; error?: string }>;
  },
  gmailTriageStatus(): Promise<GmailTriageStatus> {
    return fetch(`/api/gmail-triage/status`, { credentials: "same-origin" }).then(json) as Promise<GmailTriageStatus>;
  },
  gmailTriageSaveSettings(patch: Partial<GmailTriageSettings>): Promise<{ ok: boolean; settings: GmailTriageSettings }> {
    return fetch(`/api/gmail-triage/settings`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(patch),
    }).then(json) as Promise<{ ok: boolean; settings: GmailTriageSettings }>;
  },
  gmailTriageRun(limit: number, mode: "dry_run" | "live"): Promise<{ ok: boolean; result: GmailTriageRunResult }> {
    return fetch(`/api/gmail-triage/run`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ limit, mode }),
    }).then(json) as Promise<{ ok: boolean; result: GmailTriageRunResult }>;
  },
  gmailTriageActions(limit = 200): Promise<{ actions: GmailTriageAction[] }> {
    return fetch(`/api/gmail-triage/actions?limit=${limit}`, { credentials: "same-origin" }).then(json) as Promise<{ actions: GmailTriageAction[] }>;
  },
  gmailTriageRevert(id: string): Promise<{ ok: boolean }> {
    return fetch(`/api/gmail-triage/actions/${encodeURIComponent(id)}/revert`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  gmailTriageFingerprints(): Promise<{ fingerprints: GmailTriageFingerprint[] }> {
    return fetch(`/api/gmail-triage/fingerprints`, { credentials: "same-origin" }).then(json) as Promise<{ fingerprints: GmailTriageFingerprint[] }>;
  },
  gmailTriageForget(id: string): Promise<{ ok: boolean; removed: number }> {
    return fetch(`/api/gmail-triage/fingerprints/${encodeURIComponent(id)}/forget`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; removed: number }>;
  },
  inboxCleanupSummary(): Promise<InboxCleanupSummary> {
    return fetch(`/api/inbox-cleanup/summary`, { credentials: "same-origin" }).then(json) as Promise<InboxCleanupSummary>;
  },
  inboxCleanupCandidates(bucket: "all" | "transactions" | "recycle_bin", limit = 100): Promise<InboxCleanupCandidatesResponse> {
    return fetch(`/api/inbox-cleanup/candidates?bucket=${encodeURIComponent(bucket)}&limit=${limit}`, { credentials: "same-origin" }).then(json) as Promise<InboxCleanupCandidatesResponse>;
  },
  inboxCleanupApproveBatch(bucket: "transactions" | "recycle_bin", limit: number, dryRun: boolean): Promise<InboxCleanupApproveResult> {
    return fetch(`/api/inbox-cleanup/approve-batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ bucket, limit, dry_run: dryRun }),
    }).then(json) as Promise<InboxCleanupApproveResult>;
  },
  inboxCleanupAllowlist(): Promise<{ ok: boolean; allowlist: { preserve_senders: string[] }; defaults: { preserve_senders: string[] } }> {
    return fetch(`/api/inbox-cleanup/allowlist`, { credentials: "same-origin" }).then(json) as Promise<{ ok: boolean; allowlist: { preserve_senders: string[] }; defaults: { preserve_senders: string[] } }>;
  },
  inboxCleanupSaveAllowlist(preserveSenders: string[]): Promise<{ ok: boolean; allowlist: { preserve_senders: string[] } }> {
    return fetch(`/api/inbox-cleanup/allowlist`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ preserve_senders: preserveSenders }),
    }).then(json) as Promise<{ ok: boolean; allowlist: { preserve_senders: string[] } }>;
  },
  inboxCleanupUnsubCandidates(limit = 100): Promise<{ ok: boolean; total: number; returned: number; items: UnsubscribeCandidateView[] }> {
    return fetch(`/api/inbox-cleanup/unsubscribe-candidates?limit=${limit}`, { credentials: "same-origin" }).then(json) as Promise<{ ok: boolean; total: number; returned: number; items: UnsubscribeCandidateView[] }>;
  },
  inboxCleanupUnsubApprove(opts: { domains?: string[]; all?: boolean; limit?: number; dryRun: boolean }): Promise<InboxCleanupUnsubResult> {
    return fetch(`/api/inbox-cleanup/unsubscribe-approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ domains: opts.domains, all: opts.all, limit: opts.limit, dry_run: opts.dryRun }),
    }).then(json) as Promise<InboxCleanupUnsubResult>;
  },
  inboxCleanupUnsubLog(): Promise<{ ok: boolean; newly_flagged_resubscriptions: number; entries: UnsubscribeLogEntry[] }> {
    return fetch(`/api/inbox-cleanup/unsubscribe-log`, { credentials: "same-origin" }).then(json) as Promise<{ ok: boolean; newly_flagged_resubscriptions: number; entries: UnsubscribeLogEntry[] }>;
  },
  inboxCleanupResubscribe(logId: string): Promise<{ ok: boolean }> {
    return fetch(`/api/inbox-cleanup/resubscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ log_id: logId }),
    }).then(json) as Promise<{ ok: boolean }>;
  },
  connectorCatalog(): Promise<ConnectorCatalogResponse> {
    return fetch(`/api/connectors/catalog`, { credentials: "same-origin" }).then(json) as Promise<ConnectorCatalogResponse>;
  },
  requestConnector(slug: string): Promise<{ ok: boolean; slug: string; requested: boolean; votes: number }> {
    return fetch(`/api/connectors/catalog/${encodeURIComponent(slug)}/request`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; slug: string; requested: boolean; votes: number }>;
  },
  configureConnector(
    slug: string,
    body: {
      account_label: string;
      account_identifier: string;
      scope: string;
      sub_scope?: string | null;
      additional_tags?: string[];
    },
  ): Promise<{
    ok: boolean;
    slug: string;
    id: string;
    account_label: string;
    account_identifier: string;
    scope: string;
  }> {
    return fetch(`/api/connectors/${encodeURIComponent(slug)}/configure`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(body),
    }).then(json) as Promise<{
      ok: boolean;
      slug: string;
      id: string;
      account_label: string;
      account_identifier: string;
      scope: string;
    }>;
  },
  connectorExportStatus(slug: string): Promise<{
    ok: boolean;
    slug: string;
    latest: {
      id: string;
      status: string;
      items_total: number;
      items_new: number;
      items_known: number;
      uploaded_at: string;
      finished_at: string | null;
      error: string | null;
    } | null;
  }> {
    return fetch(`/api/connectors/${encodeURIComponent(slug)}/export-status`, {
      credentials: "same-origin",
    }).then(json) as Promise<{
      ok: boolean;
      slug: string;
      latest: {
        id: string;
        status: string;
        items_total: number;
        items_new: number;
        items_known: number;
        uploaded_at: string;
        finished_at: string | null;
        error: string | null;
      } | null;
    }>;
  },
  uploadConnectorExport(
    slug: string,
    file: File,
  ): Promise<{ ok: boolean; slug: string; importId: string; status: string }> {
    const fd = new FormData();
    fd.append("file", file);
    return fetch(`/api/connectors/${encodeURIComponent(slug)}/import-export`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
      body: fd,
    }).then(json) as Promise<{ ok: boolean; slug: string; importId: string; status: string }>;
  },
  runConnectors(): Promise<{ ok: boolean; summary: Record<string, { new: number; seen: number; error?: string }> }> {
    return fetch(`/api/connectors/run-all`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; summary: Record<string, { new: number; seen: number; error?: string }> }>;
  },
  backlog(): Promise<{ jobs: BacklogJob[] }> {
    return fetch(`/api/backlog/jobs`, { credentials: "same-origin" }).then(json) as Promise<{ jobs: BacklogJob[] }>;
  },
  backlogActions(): Promise<{ actions: Array<{ kind: string; title: string; description: string; requiresBulkConfirmation: boolean }> }> {
    return fetch(`/api/backlog/actions`, { credentials: "same-origin" }).then(json) as Promise<{ actions: Array<{ kind: string; title: string; description: string; requiresBulkConfirmation: boolean }> }>;
  },
  backlogJob(id: string): Promise<{ job: BacklogJob }> {
    return fetch(`/api/backlog/jobs/${encodeURIComponent(id)}`, { credentials: "same-origin" }).then(json) as Promise<{ job: BacklogJob }>;
  },
  backlogEstimate(kind: string): Promise<{ ok: boolean; jobId: string }> {
    return fetch(`/api/backlog/jobs/estimate`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ kind }),
    }).then(json) as Promise<{ ok: boolean; jobId: string }>;
  },
  backlogApprove(jobId: string): Promise<{ ok: boolean }> {
    return fetch(`/api/backlog/jobs/${encodeURIComponent(jobId)}/approve`, {
      method: "POST",
      headers: csrfHeaders(),
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  backlogPause(jobId: string): Promise<{ ok: boolean }> {
    return fetch(`/api/backlog/jobs/${encodeURIComponent(jobId)}/pause`, {
      method: "POST",
      headers: csrfHeaders(),
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  backlogResume(jobId: string): Promise<{ ok: boolean }> {
    return fetch(`/api/backlog/jobs/${encodeURIComponent(jobId)}/resume`, {
      method: "POST",
      headers: csrfHeaders(),
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  backlogCancel(jobId: string): Promise<{ ok: boolean }> {
    return fetch(`/api/backlog/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: "POST",
      headers: csrfHeaders(),
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  reanalyze(sinceDays: number): Promise<{ id: string; queued: number }> {
    return fetch(`/api/backlog/reanalyze`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ sinceDays }),
    }).then(json) as Promise<{ id: string; queued: number }>;
  },
  ingestionPreview(args: { text: string; scopes?: string[] }): Promise<IngestionPreview> {
    return fetch(`/api/agents/ingestion/preview`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(args),
    }).then(json) as Promise<IngestionPreview>;
  },
  vaultTree(path = ""): Promise<{ root: string; path: string; entries: VaultEntry[] }> {
    const q = path ? `?path=${encodeURIComponent(path)}` : "";
    return fetch(`/api/vault/tree${q}`, { credentials: "same-origin" }).then(json) as Promise<{ root: string; path: string; entries: VaultEntry[] }>;
  },
  vaultFile(path: string): Promise<{ path: string; size: number; mtime: string; body: string }> {
    return fetch(`/api/vault/file?path=${encodeURIComponent(path)}`, { credentials: "same-origin" }).then(json) as Promise<{ path: string; size: number; mtime: string; body: string }>;
  },
  timeline(days = 30): Promise<{ days: number; since: string; buckets: { date: string; count: number }[]; items: CaptureRow[] }> {
    return fetch(`/api/timeline?days=${days}`, { credentials: "same-origin" }).then(json) as Promise<{ days: number; since: string; buckets: { date: string; count: number }[]; items: CaptureRow[] }>;
  },
  exploreDocuments(): Promise<{ documents: ExploreDocument[] }> {
    return fetch(`/api/explore/documents`, { credentials: "same-origin" }).then(json) as Promise<{ documents: ExploreDocument[] }>;
  },
  exploreMedia(mime?: string): Promise<{ media: ExploreMedia[] }> {
    const q = mime ? `?mime=${encodeURIComponent(mime)}` : "";
    return fetch(`/api/explore/media${q}`, { credentials: "same-origin" }).then(json) as Promise<{ media: ExploreMedia[] }>;
  },
  exploreQuotes(): Promise<{ quotes: ExploreQuote[] }> {
    return fetch(`/api/explore/quotes`, { credentials: "same-origin" }).then(json) as Promise<{ quotes: ExploreQuote[] }>;
  },
  graphNeighbours(id: string, depth = 1): Promise<{ rootId: string; depth: number; source: string; nodes: GraphResponse["nodes"]; edges: GraphResponse["edges"] }> {
    return fetch(`/api/graph/${encodeURIComponent(id)}/neighbours?depth=${depth}`, { credentials: "same-origin" }).then(json) as Promise<{ rootId: string; depth: number; source: string; nodes: GraphResponse["nodes"]; edges: GraphResponse["edges"] }>;
  },
  peopleList(args: { sort?: PeopleSort; search?: string; relationship?: string; limit?: number; offset?: number; include_test?: boolean } = {}): Promise<PeopleListResponse> {
    const params = new URLSearchParams();
    if (args.sort) params.set("sort", args.sort);
    if (args.search) params.set("search", args.search);
    if (args.relationship) params.set("relationship", args.relationship);
    if (args.limit != null) params.set("limit", String(args.limit));
    if (args.offset != null) params.set("offset", String(args.offset));
    if (args.include_test) params.set("include_test", "1");
    const qs = params.toString();
    return fetch(`/api/people${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(json) as Promise<PeopleListResponse>;
  },
  personGet(id: string): Promise<PersonDetailResponse> {
    return fetch(`/api/people/${encodeURIComponent(id)}`, { credentials: "same-origin" }).then(json) as Promise<PersonDetailResponse>;
  },
  personSentimentTrend(id: string, windows = 12): Promise<SentimentTrendResponse> {
    return fetch(
      `/api/people/${encodeURIComponent(id)}/sentiment-trend?windows=${windows}`,
      { credentials: "same-origin" },
    ).then(json) as Promise<SentimentTrendResponse>;
  },
  sentimentNeedsAttention(): Promise<SentimentNeedsAttentionResponse> {
    return fetch(`/api/sentiment/needs-attention`, { credentials: "same-origin" }).then(
      json,
    ) as Promise<SentimentNeedsAttentionResponse>;
  },
  nodeNoteGet(idOrPersonId: string): Promise<{ note: NodeNote | null; hidden_by_scope?: boolean }> {
    return fetch(`/api/nodes/${encodeURIComponent(idOrPersonId)}/note`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ note: NodeNote | null; hidden_by_scope?: boolean }>;
  },
  nodeNoteRefresh(idOrPersonId: string): Promise<{ ok: boolean; changed: boolean }> {
    return fetch(`/api/nodes/${encodeURIComponent(idOrPersonId)}/note/refresh`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; changed: boolean }>;
  },
  personCreate(payload: PersonInput): Promise<{ person: Person }> {
    return fetch(`/api/people`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    }).then(json) as Promise<{ person: Person }>;
  },
  personUpdate(id: string, patch: Partial<PersonInput>): Promise<{ person: Person }> {
    return fetch(`/api/people/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(patch),
    }).then(json) as Promise<{ person: Person }>;
  },
  personDelete(id: string): Promise<{ ok: boolean }> {
    return fetch(`/api/people/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  peopleExtract(): Promise<{ ok: boolean; scanned: number; peopleNew: number; peopleUpdated: number; interactionsNew: number }> {
    return fetch(`/api/people/extract`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; scanned: number; peopleNew: number; peopleUpdated: number; interactionsNew: number }>;
  },
  personFieldsGet(id: string, opts: { include_rejected?: boolean } = {}): Promise<PersonFieldsResponse> {
    const params = new URLSearchParams();
    if (opts.include_rejected) params.set("include_rejected", "1");
    const qs = params.toString();
    return fetch(`/api/people/${encodeURIComponent(id)}/fields${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(json) as Promise<PersonFieldsResponse>;
  },
  personFieldObserve(id: string, payload: { field: PeopleFieldName; value: string; source?: string; confirmed?: boolean; confidence?: number; meta?: Record<string, unknown> }): Promise<{ observation: FieldObservation }> {
    return fetch(`/api/people/${encodeURIComponent(id)}/fields/observe`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    }).then(json) as Promise<{ observation: FieldObservation }>;
  },
  personFieldConfirm(observationId: string): Promise<{ observation: FieldObservation }> {
    return fetch(`/api/people/fields/${encodeURIComponent(observationId)}/confirm`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ observation: FieldObservation }>;
  },
  personFieldReject(observationId: string): Promise<{ ok: boolean }> {
    return fetch(`/api/people/fields/${encodeURIComponent(observationId)}/reject`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  /** Tier-1 confirmation: the standard happy-path action. Flips the
   *  contact-level is_fully_confirmed flag and bulk-confirms every
   *  current field winner in one tap. */
  personConfirmContact(id: string): Promise<{ ok: boolean; confirmedFields: number; alreadyFullyConfirmed: boolean }> {
    return fetch(`/api/people/${encodeURIComponent(id)}/confirm-contact`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; confirmedFields: number; alreadyFullyConfirmed: boolean }>;
  },
  /** Tier-2 change resolution. resolution = "replace" (default for
   *  single-valued fields), "keep_both" (multi-valued), "reject". */
  personFieldResolveChange(observationId: string, resolution: "replace" | "keep_both" | "reject"): Promise<{ ok: boolean; observation: FieldObservation | null }> {
    return fetch(`/api/people/fields/${encodeURIComponent(observationId)}/resolve-change`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ resolution }),
    }).then(json) as Promise<{ ok: boolean; observation: FieldObservation | null }>;
  },
  relationshipsList(opts: { include_test?: boolean } = {}): Promise<RelationshipsResponse> {
    const params = new URLSearchParams();
    if (opts.include_test) params.set("include_test", "1");
    const qs = params.toString();
    return fetch(`/api/relationships${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(json) as Promise<RelationshipsResponse>;
  },
  relationshipCreate(payload: { person_id: string; type: RelationshipType; notes?: string; established_at?: string }): Promise<{ relationship: RelationshipRow }> {
    return fetch(`/api/relationships`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    }).then(json) as Promise<{ relationship: RelationshipRow }>;
  },
  relationshipDelete(id: string): Promise<{ ok: boolean }> {
    return fetch(`/api/relationships/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  relationshipConfirm(id: string, opts?: { type?: RelationshipType }): Promise<{ relationship: RelationshipRow }> {
    return fetch(`/api/relationships/${encodeURIComponent(id)}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(opts ?? {}),
    }).then(json) as Promise<{ relationship: RelationshipRow }>;
  },
  relationshipTaxonomy(): Promise<TaxonomyResponse> {
    return fetch(`/api/relationships/taxonomy`, { credentials: "same-origin" }).then(json) as Promise<TaxonomyResponse>;
  },
  relationshipRevisions(opts: { person_id?: string; unacked?: boolean } = {}): Promise<{ revisions: RelationshipRevision[] }> {
    const q = new URLSearchParams();
    if (opts.person_id) q.set("person_id", opts.person_id);
    if (opts.unacked) q.set("unacked", "1");
    const s = q.toString();
    return fetch(`/api/relationships/revisions${s ? `?${s}` : ""}`, { credentials: "same-origin" }).then(json) as Promise<{ revisions: RelationshipRevision[] }>;
  },
  relationshipRevisionAction(id: string, action: "ack" | "accept" | "revert"): Promise<{ ok: boolean }> {
    return fetch(`/api/relationships/revisions/${encodeURIComponent(id)}/${action}`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  relationshipPending(): Promise<{ pending: RelationshipPending[] }> {
    return fetch(`/api/relationships/pending`, { credentials: "same-origin" }).then(json) as Promise<{ pending: RelationshipPending[] }>;
  },
  relationshipPendingAction(id: string, action: "keep" | "switch" | "ignore"): Promise<{ ok: boolean }> {
    return fetch(`/api/relationships/pending/${encodeURIComponent(id)}/${action}`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  relationshipEdit(id: string, type: RelationshipType): Promise<{ relationship: RelationshipRow }> {
    return fetch(`/api/relationships/${encodeURIComponent(id)}/edit`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ type }),
    }).then(json) as Promise<{ relationship: RelationshipRow }>;
  },
  relationshipReject(id: string): Promise<{ ok: boolean }> {
    return fetch(`/api/relationships/${encodeURIComponent(id)}/reject`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  relationshipsInfer(): Promise<{ ok: boolean; scanned: number; emitted: number; updated: number; skippedHasManual: number }> {
    return fetch(`/api/relationships/infer`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; scanned: number; emitted: number; updated: number; skippedHasManual: number }>;
  },
  /** Aggressive LLM-driven relationship inference. Uses the model
   *  bound to the `relationship_inference` AI feature (default Sonnet)
   *  to pick a granular taxonomy label per contact. */
  relationshipsInferLlm(opts: { force?: boolean; person_ids?: string[]; max_contacts?: number } = {}): Promise<{
    ok: boolean;
    scanned: number;
    inferred: number;
    updated: number;
    skipped_recent: number;
    skipped_confirmed: number;
    skipped_low_confidence: number;
    errors: number;
  }> {
    return fetch(`/api/relationships/infer/llm`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(opts),
    }).then(json) as Promise<{
      ok: boolean;
      scanned: number;
      inferred: number;
      updated: number;
      skipped_recent: number;
      skipped_confirmed: number;
      skipped_low_confidence: number;
      errors: number;
    }>;
  },
  relationshipsTaxonomy(): Promise<TaxonomyResponse> {
    return fetch(`/api/relationships/taxonomy`, { credentials: "same-origin" }).then(json) as Promise<TaxonomyResponse>;
  },
  /** Cross-source contact aggregation + (optional) LLM relationship
   *  inference in a single request. Idempotent. */
  peopleAggregate(opts: { infer_relationships?: boolean; force?: boolean; max_contacts?: number; since?: string } = {}): Promise<{
    ok: boolean;
    run_id: string;
    aggregation: {
      packetsScanned: number;
      identitiesExtracted: number;
      contactsNew: number;
      contactsUpdated: number;
      identitiesMerged: number;
    };
    inference: {
      scanned: number;
      inferred: number;
      updated: number;
      skipped_recent: number;
      skipped_confirmed: number;
      skipped_low_confidence: number;
      errors: number;
    } | null;
  }> {
    return fetch(`/api/people/aggregate`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(opts),
    }).then(json) as Promise<{
      ok: boolean;
      run_id: string;
      aggregation: {
        packetsScanned: number;
        identitiesExtracted: number;
        contactsNew: number;
        contactsUpdated: number;
        identitiesMerged: number;
      };
      inference: {
        scanned: number;
        inferred: number;
        updated: number;
        skipped_recent: number;
        skipped_confirmed: number;
        skipped_low_confidence: number;
        errors: number;
      } | null;
    }>;
  },
  peopleNeedsReviewSummary(): Promise<{
    uncertain_inferences: Array<{
      id: string;
      person_id: string;
      type: string;
      relationship_label_custom: string | null;
      inference_confidence: number | null;
      inference_reasoning: string | null;
      inferred_at: string | null;
      display_name: string;
      primary_email: string | null;
      organization: string | null;
      role: string | null;
      avatar_url: string | null;
    }>;
    merge_candidates_pending: number;
  }> {
    return fetch(`/api/people/needs-review-summary`, { credentials: "same-origin" }).then(json) as Promise<{
      uncertain_inferences: Array<{
        id: string;
        person_id: string;
        type: string;
        relationship_label_custom: string | null;
        inference_confidence: number | null;
        inference_reasoning: string | null;
        inferred_at: string | null;
        display_name: string;
        primary_email: string | null;
        organization: string | null;
        role: string | null;
        avatar_url: string | null;
      }>;
      merge_candidates_pending: number;
    }>;
  },
  /** Per-packet enrichment + per-contact evidence aggregation in one
   *  round trip. Routes through the contact_context_enricher AI
   *  binding (Gemini Flash by default). */
  peopleEnrich(opts: { force?: boolean; packet_ids?: string[]; max_packets?: number } = {}): Promise<{
    ok: boolean;
    run_id: string;
    enrichment: {
      scanned: number;
      enriched: number;
      skipped_no_text: number;
      skipped_unsupported_type: number;
      errors: number;
    };
    aggregation: {
      contactsScanned: number;
      contactsUpdated: number;
      relationshipsInserted: number;
      relationshipsUpdated: number;
      domainColleagueStamped: number;
      errors: number;
    };
  }> {
    return fetch(`/api/people/enrich`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(opts),
    }).then(json) as Promise<{
      ok: boolean;
      run_id: string;
      enrichment: {
        scanned: number;
        enriched: number;
        skipped_no_text: number;
        skipped_unsupported_type: number;
        errors: number;
      };
      aggregation: {
        contactsScanned: number;
        contactsUpdated: number;
        relationshipsInserted: number;
        relationshipsUpdated: number;
        domainColleagueStamped: number;
        errors: number;
      };
    }>;
  },
  peopleEnrichmentRuns(limit = 5): Promise<{ runs: Array<{
    id: string;
    started_at: string;
    finished_at: string | null;
    packets_scanned: number;
    packets_enriched: number;
    packets_skipped: number;
    errors: number;
    contacts_updated: number;
    error_text: string | null;
  }> }> {
    return fetch(`/api/people/enrichment/runs?limit=${limit}`, { credentials: "same-origin" }).then(json) as Promise<{
      runs: Array<{
        id: string;
        started_at: string;
        finished_at: string | null;
        packets_scanned: number;
        packets_enriched: number;
        packets_skipped: number;
        errors: number;
        contacts_updated: number;
        error_text: string | null;
      }>;
    }>;
  },
  /** Re-aggregate evidence without re-running the LLM. Use when
   *  domain rules change. */
  peopleEnrichmentAggregate(): Promise<{ ok: boolean; contactsScanned: number; contactsUpdated: number; relationshipsInserted: number; relationshipsUpdated: number; domainColleagueStamped: number }> {
    return fetch(`/api/people/enrich/aggregate`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; contactsScanned: number; contactsUpdated: number; relationshipsInserted: number; relationshipsUpdated: number; domainColleagueStamped: number }>;
  },
  knownDomainsList(): Promise<{ domains: Array<{ id: string; domain: string; kind: "work" | "personal" | "other"; source: "auto" | "manual"; notes: string | null; created_at: string }> }> {
    return fetch(`/api/people/known-domains`, { credentials: "same-origin" }).then(json) as Promise<{ domains: Array<{ id: string; domain: string; kind: "work" | "personal" | "other"; source: "auto" | "manual"; notes: string | null; created_at: string }> }>;
  },
  knownDomainUpsert(domain: string, kind: "work" | "personal" | "other", notes?: string): Promise<{ domain: { id: string; domain: string; kind: string; source: string; notes: string | null; created_at: string } }> {
    return fetch(`/api/people/known-domains`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ domain, kind, notes }),
    }).then(json) as Promise<{ domain: { id: string; domain: string; kind: string; source: string; notes: string | null; created_at: string } }>;
  },
  knownDomainDelete(domain: string): Promise<{ ok: boolean }> {
    return fetch(`/api/people/known-domains/${encodeURIComponent(domain)}`, {
      method: "DELETE",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  conversationQuery(args: { query: string; conversation_id?: string; scope?: string }): Promise<ConversationQueryResponse> {
    return fetch(`/api/conversation/query`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(args),
    }).then(json) as Promise<ConversationQueryResponse>;
  },
  conversationFeedback(args: { message_id: string; rating: -1 | 0 | 1; comment?: string }): Promise<{ ok: boolean }> {
    return fetch(`/api/conversation/feedback`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(args),
    }).then(json) as Promise<{ ok: boolean }>;
  },
  conversationList(
    limit = 20,
    opts: { include_test?: boolean } = {},
  ): Promise<{ conversations: ConversationSummary[] }> {
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    if (opts.include_test) params.set("include_test", "1");
    return fetch(`/api/conversation/list?${params.toString()}`, { credentials: "same-origin" }).then(json) as Promise<{ conversations: ConversationSummary[] }>;
  },
  conversationGet(id: string): Promise<ConversationDetail> {
    return fetch(`/api/conversation/${encodeURIComponent(id)}`, { credentials: "same-origin" }).then(json) as Promise<ConversationDetail>;
  },
  /** SSE-streaming variant of /conversation/query. Emits meta ->
   *  retrieval -> token* -> done. The caller's onEvent callback fires
   *  for every event; resolves when the stream completes. Falls back
   *  to the JSON path when the runtime doesn't support fetch streams
   *  (very old Safari) - rare enough that we keep the fallback small. */
  conversationQueryStream(
    args: { query: string; conversation_id?: string; scope?: string },
    onEvent: (event: ConversationStreamEvent) => void,
  ): Promise<void> {
    return runConversationStream(args, onEvent);
  },

  // ===== Work-surface connectors (Worker I lane) =====
  // Thin pass-through to keep the import surface consistent. Single
  // source of truth still lives in
  // `anchors/settings/connections/api.ts` so the component can ship
  // independently; this re-export lets new callers follow the
  // `import { api } from "lib/api"` convention.
  workConnectorsList(): ReturnType<typeof workConnectorsApi.list> {
    return workConnectorsApi.list();
  },
  workConnectorUpdate(kind: string, body: { enabled?: boolean; config?: Record<string, unknown> }) {
    return workConnectorsApi.update(kind, body);
  },
  workConnectorRunNow(kind: string) {
    return workConnectorsApi.runNow(kind);
  },
  workConnectorRuns(kind: string) {
    return workConnectorsApi.runs(kind);
  },
  workConnectorRotateSecret(kind: string) {
    return workConnectorsApi.rotateSecret(kind);
  },
  workConnectorVerifyReadState(kind: string) {
    return workConnectorsApi.verifyReadState(kind);
  },
  briefLatest() {
    return briefApi.latest();
  },
  briefAssemble() {
    return briefApi.assemble();
  },
  // ----- Phase 2 inferred-vs-confirmed -----------------------------
  needsReview(): Promise<NeedsReviewResponse> {
    return fetch(`/api/people/needs-review`, { credentials: "same-origin" }).then(json) as Promise<NeedsReviewResponse>;
  },
  // Tags
  inferenceTagConfirm(id: string): Promise<{ tag: TagRow }> {
    return fetch(`/api/inference/tag/${encodeURIComponent(id)}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ tag: TagRow }>;
  },
  inferenceTagReject(id: string): Promise<{ tag: TagRow }> {
    return fetch(`/api/inference/tag/${encodeURIComponent(id)}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ tag: TagRow }>;
  },
  inferenceTagPatch(id: string, patch: { name?: string }): Promise<{ tag: TagRow }> {
    return fetch(`/api/inference/tag/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(patch),
    }).then(json) as Promise<{ tag: TagRow }>;
  },
  inferenceTagsFor(target: "capture" | "person" | "event", id: string): Promise<{ tags: TagRow[] }> {
    const params = new URLSearchParams({ target, id });
    return fetch(`/api/inference/tags?${params.toString()}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ tags: TagRow[] }>;
  },
  inferenceTagAdd(args: { target_kind: "capture" | "person" | "event"; target_id: string; name: string }): Promise<{ tag: TagRow }> {
    return fetch(`/api/inference/tags`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(args),
    }).then(json) as Promise<{ tag: TagRow }>;
  },
  // Capture scope
  inferenceScopeConfirm(captureId: string): Promise<{ ok: boolean }> {
    return fetch(`/api/inference/scope/${encodeURIComponent(captureId)}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  inferenceScopeReclassify(captureId: string, scope: string): Promise<{ ok: boolean; previousScope?: string }> {
    return fetch(`/api/inference/scope/${encodeURIComponent(captureId)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ scope }),
    }).then(json) as Promise<{ ok: boolean; previousScope?: string }>;
  },
  // Merge candidates
  inferenceMergeList(): Promise<{ candidates: MergeCandidateRow[] }> {
    return fetch(`/api/inference/merge`, { credentials: "same-origin" }).then(json) as Promise<{ candidates: MergeCandidateRow[] }>;
  },
  inferenceMergeRun(): Promise<{ ok: boolean; scanned: number; candidatesNew: number; candidatesExisting: number; candidatesSkippedRejected: number }> {
    return fetch(`/api/inference/merge/run`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; scanned: number; candidatesNew: number; candidatesExisting: number; candidatesSkippedRejected: number }>;
  },
  inferenceMergeConfirm(id: string, keepPersonId?: string): Promise<MergeExecutionResult> {
    return fetch(`/api/inference/merge/${encodeURIComponent(id)}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(keepPersonId ? { keep_person_id: keepPersonId } : {}),
    }).then(json) as Promise<MergeExecutionResult>;
  },
  inferenceMergeReject(id: string): Promise<{ candidate: MergeCandidateRow }> {
    return fetch(`/api/inference/merge/${encodeURIComponent(id)}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ candidate: MergeCandidateRow }>;
  },

  // ── Cross-account identity unification ───────────────────────
  identityMergeCandidates(
    status = "pending",
  ): Promise<{ candidates: IdentityMergeCandidate[] }> {
    return fetch(
      `/api/people/merge-candidates?status=${encodeURIComponent(status)}`,
      { credentials: "same-origin" },
    ).then(json) as Promise<{ candidates: IdentityMergeCandidate[] }>;
  },
  identityMergeAccept(
    id: string,
  ): Promise<{ ok: boolean; survivor_id: string; merged: string }> {
    return fetch(
      `/api/people/merge-candidates/${encodeURIComponent(id)}/accept`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        credentials: "same-origin",
      },
    ).then(json) as Promise<{
      ok: boolean;
      survivor_id: string;
      merged: string;
    }>;
  },
  identityMergeReject(id: string): Promise<{ ok: boolean }> {
    return fetch(
      `/api/people/merge-candidates/${encodeURIComponent(id)}/reject`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        credentials: "same-origin",
      },
    ).then(json) as Promise<{ ok: boolean }>;
  },
  personIdentityLinks(
    personId: string,
  ): Promise<{ links: IdentityLinkRow[] }> {
    return fetch(
      `/api/people/${encodeURIComponent(personId)}/identity-links`,
      { credentials: "same-origin" },
    ).then(json) as Promise<{ links: IdentityLinkRow[] }>;
  },

  // ── Subscription audit ───────────────────────────────────────
  subscriptions(
    activeOnly = true,
  ): Promise<{ subscriptions: SubscriptionRow[] }> {
    return fetch(
      `/api/subscriptions?active=${activeOnly ? "true" : "false"}`,
      { credentials: "same-origin" },
    ).then(json) as Promise<{ subscriptions: SubscriptionRow[] }>;
  },
  subscriptionsSummary(): Promise<SubscriptionSummary> {
    return fetch(`/api/subscriptions/summary`, {
      credentials: "same-origin",
    }).then(json) as Promise<SubscriptionSummary>;
  },
  subscriptionMarkCancelled(id: string): Promise<{ ok: boolean }> {
    return fetch(
      `/api/subscriptions/${encodeURIComponent(id)}/mark-cancelled`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        credentials: "same-origin",
      },
    ).then(json) as Promise<{ ok: boolean }>;
  },
  subscriptionDismissRecommendation(
    id: string,
  ): Promise<{ ok: boolean }> {
    return fetch(
      `/api/subscriptions/${encodeURIComponent(id)}/dismiss-recommendation`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        credentials: "same-origin",
      },
    ).then(json) as Promise<{ ok: boolean }>;
  },

  // ── End-of-day reflections ───────────────────────────────────
  reflectionToday(): Promise<{ reflection: ReflectionRow | null }> {
    return fetch(`/api/reflections/today`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ reflection: ReflectionRow | null }>;
  },
  reflectionsList(
    from?: string,
    to?: string,
  ): Promise<{ reflections: ReflectionRow[] }> {
    const q = new URLSearchParams();
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    return fetch(`/api/reflections?${q.toString()}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ reflections: ReflectionRow[] }>;
  },
  reflectionStats(days = 30): Promise<ReflectionStats> {
    return fetch(`/api/reflections/stats?days=${days}`, {
      credentials: "same-origin",
    }).then(json) as Promise<ReflectionStats>;
  },
  saveReflection(
    body: ReflectionInput,
  ): Promise<{
    reflection: ReflectionRow;
    packet_id: string;
    engagement_hint: { id: string; title: string } | null;
  }> {
    return fetch(`/api/reflections`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(body),
    }).then(json) as Promise<{
      reflection: ReflectionRow;
      packet_id: string;
      engagement_hint: { id: string; title: string } | null;
    }>;
  },

  // ── Wiki [[EntityName]] links ────────────────────────────────
  wikiResolve(
    names: string[],
  ): Promise<{ resolved: Record<string, WikiResolveResult | null> }> {
    return fetch(
      `/api/wiki-link/resolve?names=${encodeURIComponent(names.join(","))}`,
      { credentials: "same-origin" },
    ).then(json) as Promise<{
      resolved: Record<string, WikiResolveResult | null>;
    }>;
  },
  wikiSuggest(q: string): Promise<{ suggestions: WikiCandidate[] }> {
    return fetch(`/api/wiki-link/suggest?q=${encodeURIComponent(q)}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ suggestions: WikiCandidate[] }>;
  },
  wikiCreatePlaceholder(
    name: string,
    kind: string,
  ): Promise<{ id: string; display_name: string; kind: string; created: boolean }> {
    return fetch(`/api/wiki-link/create-placeholder`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ name, kind }),
    }).then(json) as Promise<{
      id: string;
      display_name: string;
      kind: string;
      created: boolean;
    }>;
  },
  companionPair(deviceName: string): Promise<{
    ok: boolean;
    device_id: string;
    device_name: string;
    token: string;
  }> {
    return fetch(`/api/settings/companion-pair`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ device_name: deviceName }),
    }).then(json) as Promise<{
      ok: boolean;
      device_id: string;
      device_name: string;
      token: string;
    }>;
  },
  companionDevices(): Promise<{
    ok: boolean;
    devices: Array<{
      id: string;
      device_name: string;
      paired_at: string;
      last_sync_at: string | null;
    }>;
  }> {
    return fetch(`/api/settings/companion-pair/devices`, {
      credentials: "same-origin",
    }).then(json) as Promise<{
      ok: boolean;
      devices: Array<{
        id: string;
        device_name: string;
        paired_at: string;
        last_sync_at: string | null;
      }>;
    }>;
  },
  companionUnpair(id: string): Promise<{ ok: boolean; removed: number }> {
    return fetch(`/api/settings/companion-pair/devices/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean; removed: number }>;
  },
  wikiBacklinks(args: {
    entity_type?: string;
    entity_id?: string;
    name?: string;
  }): Promise<{ backlinks: WikiBacklinkRow[]; count: number }> {
    const q = new URLSearchParams();
    if (args.entity_type) q.set("entity_type", args.entity_type);
    if (args.entity_id) q.set("entity_id", args.entity_id);
    if (args.name) q.set("name", args.name);
    return fetch(`/api/wiki-link/backlinks?${q.toString()}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{
      backlinks: WikiBacklinkRow[];
      count: number;
    }>;
  },

  // ── Unified actions tracker ──────────────────────────────────
  unifiedActions(params: {
    status?: string;
    kinds?: string[];
    surface?: string[];
    person_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ actions: UnifiedActionRow[]; total: number }> {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.kinds?.length) q.set("kinds", params.kinds.join(","));
    if (params.surface?.length) q.set("surface", params.surface.join(","));
    if (params.person_id) q.set("person_id", params.person_id);
    if (params.limit != null) q.set("limit", String(params.limit));
    if (params.offset != null) q.set("offset", String(params.offset));
    return fetch(`/api/actions/unified?${q.toString()}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ actions: UnifiedActionRow[]; total: number }>;
  },
  unifiedActionCounts(): Promise<{
    counts: Record<string, Record<string, number>>;
  }> {
    return fetch(`/api/actions/counts`, {
      credentials: "same-origin",
    }).then(json) as Promise<{
      counts: Record<string, Record<string, number>>;
    }>;
  },
  unifiedActionAudit(
    sourceTable: string,
    sourceId: string,
  ): Promise<{ audit: Array<Record<string, unknown>> }> {
    return fetch(
      `/api/actions/${encodeURIComponent(sourceTable)}/${encodeURIComponent(sourceId)}/audit`,
      { credentials: "same-origin" },
    ).then(json) as Promise<{ audit: Array<Record<string, unknown>> }>;
  },
  unifiedActionDo(
    sourceTable: string,
    sourceId: string,
    verb: "accept" | "dismiss" | "snooze",
    body?: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return fetch(
      `/api/actions/${encodeURIComponent(sourceTable)}/${encodeURIComponent(sourceId)}/${verb}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        credentials: "same-origin",
        body: JSON.stringify(body ?? {}),
      },
    ).then(json) as Promise<Record<string, unknown>>;
  },

  // ── Macro privacy views ──────────────────────────────────────
  views(): Promise<{ views: ViewDef[]; scopes: ScopeDef[]; activeViewId: string }> {
    return fetch("/api/views", { credentials: "same-origin" }).then(json) as Promise<{
      views: ViewDef[];
      scopes: ScopeDef[];
      activeViewId: string;
    }>;
  },
  setActiveView(viewId: string): Promise<{ view: ViewDef }> {
    return fetch("/api/views/active", {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ viewId }),
    }).then(json) as Promise<{ view: ViewDef }>;
  },
  createView(name: string, scopes: string[]): Promise<{ view: ViewDef }> {
    return fetch("/api/views", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ name, scopes }),
    }).then(json) as Promise<{ view: ViewDef }>;
  },
  updateView(
    id: string,
    patch: { name?: string; scopes?: string[] },
  ): Promise<{ view: ViewDef }> {
    return fetch(`/api/views/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(patch),
    }).then(json) as Promise<{ view: ViewDef }>;
  },
  cloneView(id: string): Promise<{ view: ViewDef }> {
    return fetch(`/api/views/${encodeURIComponent(id)}/clone`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ view: ViewDef }>;
  },
  deleteView(id: string): Promise<{ ok: true }> {
    return fetch(`/api/views/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: true }>;
  },

  // --- Universal dates (Wave 7) ----------------------------------------
  datesUpcoming(days?: number): Promise<{ days: number; dates: DateItem[] }> {
    const params = new URLSearchParams();
    if (days != null) params.set("days", String(days));
    const qs = params.toString();
    return fetch(`/api/dates/upcoming${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(json) as Promise<{
      days: number;
      dates: DateItem[];
    }>;
  },
  datesBirthdays(): Promise<{ birthdays: DateItem[] }> {
    return fetch(`/api/dates/birthdays`, { credentials: "same-origin" }).then(json) as Promise<{ birthdays: DateItem[] }>;
  },
  datesAll(): Promise<{ dates: DateItem[]; count: number }> {
    return fetch(`/api/dates/all`, { credentials: "same-origin" }).then(json) as Promise<{ dates: DateItem[]; count: number }>;
  },
  datesForPerson(personId: string): Promise<{ dates: DateItem[] }> {
    return fetch(`/api/dates/person/${encodeURIComponent(personId)}`, { credentials: "same-origin" }).then(json) as Promise<{
      dates: DateItem[];
    }>;
  },
  datesNotifications(): Promise<{ notifications: DateNotification[] }> {
    return fetch(`/api/dates/notifications`, { credentials: "same-origin" }).then(json) as Promise<{
      notifications: DateNotification[];
    }>;
  },
  dateConfirm(id: string): Promise<{ date: DateItem }> {
    return fetch(`/api/dates/${encodeURIComponent(id)}/confirm`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ date: DateItem }>;
  },
  dateReject(id: string): Promise<{ ok: true }> {
    return fetch(`/api/dates/${encodeURIComponent(id)}/reject`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: true }>;
  },
  dateSnooze(id: string, days?: number): Promise<{ ok: true }> {
    return fetch(`/api/dates/${encodeURIComponent(id)}/snooze`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(days != null ? { days } : {}),
    }).then(json) as Promise<{ ok: true }>;
  },
  dateNotificationSeen(id: string): Promise<{ ok: true }> {
    return fetch(`/api/dates/notifications/${encodeURIComponent(id)}/seen`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: true }>;
  },
  dateNotificationDismiss(id: string): Promise<{ ok: true }> {
    return fetch(`/api/dates/notifications/${encodeURIComponent(id)}/dismiss`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: true }>;
  },
  intents(includeDone = false): Promise<{
    kinds: string[];
    columns: Record<string, IntentRow[]>;
    total: number;
  }> {
    const qs = includeDone ? "?include_done=1" : "";
    return fetch(`/api/intents${qs}`, { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ kinds: string[]; columns: Record<string, IntentRow[]>; total: number }>;
  },
  intentDetail(id: string): Promise<{ intent: IntentRow; research: ResearchResult[] }> {
    return fetch(`/api/intents/${encodeURIComponent(id)}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ intent: IntentRow; research: ResearchResult[] }>;
  },
  setIntentStatus(
    id: string,
    status: "open" | "confirmed" | "done" | "dismissed",
    notes?: string,
  ): Promise<{ ok: boolean; status: string }> {
    return fetch(`/api/intents/${encodeURIComponent(id)}/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ status, ...(notes ? { notes } : {}) }),
    }).then(json) as Promise<{ ok: boolean; status: string }>;
  },

  // ---- MCP clients + grants (Step 15c + W076.7 full surface) ----
  mcpClients(): Promise<{
    ok: boolean;
    clients: Array<{
      id: string;
      client_name: string;
      paired_at: string;
      last_seen_at: string | null;
      sensitivity_default: string;
      paused: number;
      revoked_at: string | null;
      granted_scopes: string[];
      granted_view_scopes: string[];
      granted_sensitivity_tiers: string[];
      grants: Array<{ resource: string; allowed: number; can_write: number; sensitivity_max: string }>;
    }>;
  }> {
    return fetch("/api/mcp/clients", { credentials: "same-origin" }).then(
      json,
    ) as Promise<{
      ok: boolean;
      clients: Array<{
        id: string;
        client_name: string;
        paired_at: string;
        last_seen_at: string | null;
        sensitivity_default: string;
        paused: number;
        revoked_at: string | null;
        granted_scopes: string[];
        granted_view_scopes: string[];
        granted_sensitivity_tiers: string[];
        grants: Array<{ resource: string; allowed: number; can_write: number; sensitivity_max: string }>;
      }>;
    }>;
  },
  mcpCreateClient(clientName: string): Promise<{
    ok: boolean;
    id: string;
    client_name: string;
    token: string;
    proposed_grants: Array<{ resource: string; allowed: number }>;
    note: string;
  }> {
    return fetch("/api/mcp/clients", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ client_name: clientName }),
    }).then(json) as Promise<{
      ok: boolean;
      id: string;
      client_name: string;
      token: string;
      proposed_grants: Array<{ resource: string; allowed: number }>;
      note: string;
    }>;
  },
  mcpSetGrants(
    id: string,
    grants: Array<{ resource: string; allowed: boolean; can_write?: boolean; sensitivity_max?: string }>,
  ): Promise<{ ok: boolean }> {
    return fetch(`/api/mcp/clients/${encodeURIComponent(id)}/grants`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ grants }),
    }).then(json) as Promise<{ ok: boolean }>;
  },
  mcpClientAudit(id: string): Promise<{
    ok: boolean;
    summary: string;
    events: Array<{
      method: string;
      resource: string | null;
      kind: string;
      allowed: number;
      at: string;
    }>;
  }> {
    return fetch(`/api/mcp/clients/${encodeURIComponent(id)}/audit`, {
      credentials: "same-origin",
    }).then(json) as Promise<{
      ok: boolean;
      summary: string;
      events: Array<{
        method: string;
        resource: string | null;
        kind: string;
        allowed: number;
        at: string;
      }>;
    }>;
  },
  mcpRevokeClient(id: string): Promise<{ ok: boolean }> {
    return fetch(`/api/mcp/clients/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  mcpPauseClient(id: string, paused: boolean): Promise<{ ok: boolean; paused: boolean }> {
    return fetch(`/api/mcp/clients/${encodeURIComponent(id)}/pause`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ paused }),
    }).then(json) as Promise<{ ok: boolean; paused: boolean }>;
  },
  mcpSetScopes(
    id: string,
    body: {
      scopes: string[];
      view_scopes: string[];
      sensitivity_tiers: string[];
      can_write_scopes?: string[];
    },
  ): Promise<{ ok: boolean; sensitivity_max: string }> {
    return fetch(`/api/mcp/clients/${encodeURIComponent(id)}/scopes`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(body),
    }).then(json) as Promise<{ ok: boolean; sensitivity_max: string }>;
  },

  // ---- Step 17: lifestyle (preferences / routines / decisions) ----
  // Bindings for the mounted /api/{preferences,routines,decisions}
  // routes (backend/src/routes/lifestyle.ts).
  preferencesList(): Promise<{ ok: boolean; preferences: Array<Record<string, unknown>> }> {
    return fetch("/api/preferences", { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ ok: boolean; preferences: Array<Record<string, unknown>> }>;
  },
  routinesList(): Promise<{ ok: boolean; routines: Array<Record<string, unknown>> }> {
    return fetch("/api/routines", { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ ok: boolean; routines: Array<Record<string, unknown>> }>;
  },
  decisionsList(): Promise<{ ok: boolean; decisions: Array<Record<string, unknown>> }> {
    return fetch("/api/decisions", { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ ok: boolean; decisions: Array<Record<string, unknown>> }>;
  },
  lifestyleAction(
    kind: "preferences" | "routines" | "decisions",
    id: string,
    action: string,
    body?: Record<string, unknown>,
  ): Promise<{ ok: boolean }> {
    return fetch(
      `/api/${kind}/${encodeURIComponent(id)}/${encodeURIComponent(action)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        credentials: "same-origin",
        body: JSON.stringify(body ?? {}),
      },
    ).then(json) as Promise<{ ok: boolean }>;
  },
  decisionCreate(
    title: string,
    description_md: string,
  ): Promise<{ ok: boolean; id: string }> {
    return fetch("/api/decisions", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ title, description_md }),
    }).then(json) as Promise<{ ok: boolean; id: string }>;
  },

  // Bindings for the mounted /api/{health-entries,time-patterns}
  // routes (backend/src/routes/health-time.ts, Step 20).
  healthEntriesList(): Promise<{ ok: boolean; entries: Array<Record<string, unknown>> }> {
    return fetch("/api/health-entries", { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ ok: boolean; entries: Array<Record<string, unknown>> }>;
  },
  timePatternsList(): Promise<{ ok: boolean; patterns: Array<Record<string, unknown>> }> {
    return fetch("/api/time-patterns", { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ ok: boolean; patterns: Array<Record<string, unknown>> }>;
  },
  healthTimeAction(
    kind: "health-entries" | "time-patterns",
    id: string,
    action: "confirm" | "reject",
  ): Promise<{ ok: boolean }> {
    return fetch(
      `/api/${kind}/${encodeURIComponent(id)}/${action}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        credentials: "same-origin",
        body: "{}",
      },
    ).then(json) as Promise<{ ok: boolean }>;
  },

  // ---- Worker supervisor / health surface (Step 18) ----
  adminWorkersFleet(): Promise<{
    ok: boolean;
    passive_mode: boolean;
    workers: Array<{
      id: string;
      label: string;
      tier: string;
      source: string;
      status: string;
      paused: boolean;
      paused_reason: string | null;
      last_tick_at: string | null;
      last_heartbeat_at: string | null;
      heartbeat_stale: boolean;
      error_count: number;
      last_error_at: string | null;
      last_error: string | null;
      total_runs: number;
      total_items_processed: number;
      readiness_score: string;
      ready_for_promotion: boolean;
      runnable_on_demand: boolean;
    }>;
    summary: Record<string, number>;
  }> {
    return fetch("/api/admin/workers/fleet", {
      credentials: "same-origin",
    }).then(json) as Promise<{
      ok: boolean;
      passive_mode: boolean;
      workers: Array<{
        id: string;
        label: string;
        tier: string;
        source: string;
        status: string;
        paused: boolean;
        paused_reason: string | null;
        last_tick_at: string | null;
        last_heartbeat_at: string | null;
        heartbeat_stale: boolean;
        error_count: number;
        last_error_at: string | null;
        last_error: string | null;
        total_runs: number;
        total_items_processed: number;
        readiness_score: string;
        ready_for_promotion: boolean;
        runnable_on_demand: boolean;
      }>;
      summary: Record<string, number>;
    }>;
  },
  adminWorkerRuns(id: string): Promise<{
    ok: boolean;
    worker_id: string;
    runs: Array<{
      id: string;
      worker_id: string;
      started_at: string;
      finished_at: string | null;
      status: string;
      items_processed: number;
      error_message: string | null;
      trigger: string;
    }>;
  }> {
    return fetch(`/api/admin/workers/${encodeURIComponent(id)}/runs`, {
      credentials: "same-origin",
    }).then(json) as Promise<{
      ok: boolean;
      worker_id: string;
      runs: Array<{
        id: string;
        worker_id: string;
        started_at: string;
        finished_at: string | null;
        status: string;
        items_processed: number;
        error_message: string | null;
        trigger: string;
      }>;
    }>;
  },
  adminWorkerPause(id: string, reason?: string): Promise<{ ok: boolean; paused: boolean }> {
    return fetch(`/api/admin/workers/${encodeURIComponent(id)}/pause`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(reason ? { reason } : {}),
    }).then(json) as Promise<{ ok: boolean; paused: boolean }>;
  },
  adminWorkerResume(id: string): Promise<{ ok: boolean; paused: boolean }> {
    return fetch(`/api/admin/workers/${encodeURIComponent(id)}/resume`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean; paused: boolean }>;
  },
  adminWorkerRunNow(id: string): Promise<{
    ok: boolean;
    worker_id: string;
    durationMs?: number;
    yielded?: boolean;
    error: string | null;
  }> {
    return fetch(`/api/admin/workers/${encodeURIComponent(id)}/run-now`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{
      ok: boolean;
      worker_id: string;
      durationMs?: number;
      yielded?: boolean;
      error: string | null;
    }>;
  },

  // ---- Q42 universal packet viewer ----
  packetSource(packetId: string): Promise<PacketSourceResolved> {
    return fetch(`/api/packets/${encodeURIComponent(packetId)}/source`, {
      credentials: "same-origin",
    }).then(json) as Promise<PacketSourceResolved>;
  },
  snoozePacket(
    packetId: string,
    opts: { hours?: number; days?: number; reason?: string },
  ): Promise<{ ok: boolean; until_at: string; hours: number }> {
    return fetch(`/api/packets/${encodeURIComponent(packetId)}/snooze`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(opts),
    }).then(json) as Promise<{ ok: boolean; until_at: string; hours: number }>;
  },
  unsnoozePacket(packetId: string): Promise<{ ok: boolean }> {
    return fetch(`/api/packets/${encodeURIComponent(packetId)}/unsnooze`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: "{}",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  agendaTrips(): Promise<{ ok: boolean; trips: TripRow[] }> {
    return fetch(`/api/agenda/trips`, { credentials: "same-origin" }).then(json) as Promise<{
      ok: boolean;
      trips: TripRow[];
    }>;
  },
  todayAttention(input: {
    surface?: string;
    cluster?: string | null;
    capsule?: string | null;
    item_kind: string;
    item_id: string;
    event_type: "view" | "open_source" | "done" | "snooze" | "dismiss" | "accept" | "undo" | "resolved" | "ignored_for_24h" | "hide_forever";
    context?: Record<string, unknown> | null;
  }): Promise<{ ok: boolean }> {
    return fetch(`/api/today/attention`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(input),
    }).then(json) as Promise<{ ok: boolean }>;
  },
  todayAttentionBatch(events: Array<{
    surface?: string;
    cluster?: string | null;
    capsule?: string | null;
    item_kind: string;
    item_id: string;
    event_type: "view" | "open_source" | "done" | "snooze" | "dismiss" | "accept" | "undo" | "resolved" | "ignored_for_24h";
    context?: Record<string, unknown> | null;
  }>): Promise<{ ok: boolean; written: number }> {
    return fetch(`/api/today/attention/batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ events }),
    }).then(json) as Promise<{ ok: boolean; written: number }>;
  },
  todayCurate(input: {
    surface?: string;
    capsule: string;
    cluster?: string | null;
    cap?: number;
    candidate_ids?: string[];
    active_scope?: string | null;
    force?: boolean;
  }): Promise<TodayCurateResult> {
    return fetch(`/api/today/curate`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify(input),
    }).then(json) as Promise<TodayCurateResult>;
  },
  // W070.7 curate-not-aggregate consumers. Each surface's /curate
  // endpoint returns { picks, <entityArray> } where the entity array
  // is already filtered + ordered to the curator's picks. The strips
  // are purely additive: on any failure or empty pick set the caller
  // renders nothing and the page's full aggregate list is the fallback.
  engagementsCurate(opts: { cap?: number; force?: boolean } = {}): Promise<CurateSurfaceResult<EngagementRow>> {
    const params = new URLSearchParams();
    if (opts.cap) params.set("cap", String(opts.cap));
    if (opts.force) params.set("force", "1");
    const qs = params.toString();
    return fetch(`/api/engagements/curate${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(
      json,
    ) as Promise<CurateSurfaceResult<EngagementRow>>;
  },
  engagementsAwaitingCurate(opts: { cap?: number; force?: boolean } = {}): Promise<CurateSurfaceResult<EngagementRow>> {
    const params = new URLSearchParams();
    if (opts.cap) params.set("cap", String(opts.cap));
    if (opts.force) params.set("force", "1");
    const qs = params.toString();
    return fetch(`/api/engagements/awaiting/curate${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(
      json,
    ) as Promise<CurateSurfaceResult<EngagementRow>>;
  },
  recommendationsCurate(opts: { cap?: number; force?: boolean } = {}): Promise<{ picks: CuratePick[]; recommendations: Recommendation[] }> {
    const params = new URLSearchParams();
    if (opts.cap) params.set("cap", String(opts.cap));
    if (opts.force) params.set("force", "1");
    const qs = params.toString();
    return fetch(`/api/recommendations/curate${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ picks: CuratePick[]; recommendations: Recommendation[] }>;
  },
  observationsCurate(opts: { cap?: number; force?: boolean } = {}): Promise<{ picks: CuratePick[]; observations: Observation[] }> {
    const params = new URLSearchParams();
    if (opts.cap) params.set("cap", String(opts.cap));
    if (opts.force) params.set("force", "1");
    const qs = params.toString();
    return fetch(`/api/observations/curate${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ picks: CuratePick[]; observations: Observation[] }>;
  },
  tripsCurate(opts: { cap?: number; force?: boolean } = {}): Promise<{ picks: CuratePick[]; trips: TripRow[] }> {
    const params = new URLSearchParams();
    if (opts.cap) params.set("cap", String(opts.cap));
    if (opts.force) params.set("force", "1");
    const qs = params.toString();
    return fetch(`/api/agenda/trips/curate${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ picks: CuratePick[]; trips: TripRow[] }>;
  },
  peopleCurate(opts: { cap?: number; force?: boolean } = {}): Promise<{ picks: CuratePick[]; people: Person[] }> {
    const params = new URLSearchParams();
    if (opts.cap) params.set("cap", String(opts.cap));
    if (opts.force) params.set("force", "1");
    const qs = params.toString();
    return fetch(`/api/people/curate${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ picks: CuratePick[]; people: Person[] }>;
  },
  thingsCurate(opts: { cap?: number; force?: boolean } = {}): Promise<{ picks: CuratePick[]; things: ThingCurateRow[] }> {
    const params = new URLSearchParams();
    if (opts.cap) params.set("cap", String(opts.cap));
    if (opts.force) params.set("force", "1");
    const qs = params.toString();
    return fetch(`/api/things/curate${qs ? `?${qs}` : ""}`, { credentials: "same-origin" }).then(
      json,
    ) as Promise<{ picks: CuratePick[]; things: ThingCurateRow[] }>;
  },
  todayPriority(): Promise<{
    ok: boolean;
    items: TodayPriorityRow[];
  }> {
    return fetch(`/api/today/priority`, { credentials: "same-origin" }).then(json) as Promise<{
      ok: boolean;
      items: TodayPriorityRow[];
    }>;
  },
  todayPriorityRecompute(): Promise<{
    ok: boolean;
    scanned: number;
    updated: number;
    escalated: number;
  }> {
    return fetch(`/api/today/priority/recompute`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{
      ok: boolean;
      scanned: number;
      updated: number;
      escalated: number;
    }>;
  },

  // ---- W076.4 attention stream ----
  attentionList(args: { surface?: string; kinds?: string[]; limit?: number } = {}): Promise<{
    items: AttentionItem[];
    count: number;
  }> {
    const qs = new URLSearchParams();
    if (args.surface) qs.set("surface", args.surface);
    if (args.limit) qs.set("limit", String(args.limit));
    if (args.kinds) for (const k of args.kinds) qs.append("kind", k);
    const suffix = qs.toString();
    return fetch(`/api/attention${suffix ? `?${suffix}` : ""}`, {
      credentials: "same-origin",
    }).then(json) as Promise<{ items: AttentionItem[]; count: number }>;
  },
  attentionDismiss(id: string): Promise<{ ok: boolean }> {
    return fetch(`/api/attention/${encodeURIComponent(id)}/dismiss`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },
  attentionActedOn(id: string): Promise<{ ok: boolean }> {
    return fetch(`/api/attention/${encodeURIComponent(id)}/acted-on`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "same-origin",
    }).then(json) as Promise<{ ok: boolean }>;
  },

  // ---- Recall entity-aware ----
  recallEntityAware(query: string): Promise<RecallEntityAwareResponse> {
    return fetch(`/api/recall/entity-aware`, {
      method: "POST",
      headers: { "content-type": "application/json", ...csrfHeaders() },
      credentials: "same-origin",
      body: JSON.stringify({ query }),
    }).then(json) as Promise<RecallEntityAwareResponse>;
  },
};

export type AttentionItemKind =
  | "engagement"
  | "recommendation"
  | "brief_item"
  | "birthday"
  | "commitment"
  | "expiring"
  | "follow_up";

export interface AttentionItem {
  id: string;
  userId: string;
  kind: AttentionItemKind;
  sourceRef: string;
  subjectEntityKind: string | null;
  subjectEntityId: string | null;
  priorityScore: number;
  title: string | null;
  summary: string | null;
  reason: string | null;
  surfaceHints: string[];
  payload: Record<string, unknown> | null;
  expiresAt: string | null;
  deliveredAt: string | null;
  dismissedAt: string | null;
  actedOnAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecallCitation {
  ref_kind: string;
  ref_id: string;
  label: string;
  date: string | null;
}

export interface RecallEntityAwareResponse {
  ok: boolean;
  fellBack?: boolean;
  query?: string;
  intent?: string;
  anchors?: Array<{ kind: string; label: string }>;
  resolved?: Array<{ id: string; type: string; display_name: string }>;
  disambiguation?: Array<{ id: string; type: string; display_name: string }> | null;
  aggregation?: Record<string, unknown>;
  citations?: RecallCitation[];
  answer?: string;
  answer_source?: "llm" | "deterministic";
  reason?: string;
}

export type TodayPriorityState = "low" | "medium" | "high" | "critical";

export interface TodayPriorityRow {
  item_id: string;
  priority_state: TodayPriorityState;
  priority_score: number;
  last_escalated_at: string | null;
  escalation_reason: string | null;
}

/** A single curator pick: the entity id, its rank in the curated set,
 *  and a one-sentence reason the curator chose it. Shared by every
 *  W070.7 /curate consumer. */
export interface CuratePick {
  id: string;
  rank: number;
  reason: string;
}

/** Generic shape of a surface /curate response that returns picks plus
 *  the matching engagement rows (engagements + awaiting share it). */
export interface CurateSurfaceResult<T> {
  picks: CuratePick[];
  engagements: T[];
}

/** Loose row shape for /things/curate. The Things page models a thing
 *  locally; the curate endpoint returns the same column set. */
export interface ThingCurateRow {
  id?: string;
  name?: string;
  label?: string;
  brand?: string | null;
  last_seen_at?: string | null;
  created_at?: string | null;
  transaction_count?: number | null;
}

export interface TodayCurateResult {
  picks: Array<{ id: string; rank: number; reason: string }>;
  rationale: string;
  model_used: string | null;
  picked_at: string;
  cached: boolean;
  fallback_reason?: string;
}

export interface TripRow {
  id: string;
  title: string | null;
  destination: string | null;
  origin: string | null;
  start_at: string | null;
  end_at: string | null;
  segment_count: number | null;
  booking_count: number | null;
  provider: string | null;
  confirmed: number;
  props_json: string | null;
}

// ---- Q42 universal packet viewer types ----

export type PacketRenderKind =
  | "outlook-email"
  | "outlook-calendar"
  | "outlook-task"
  | "gmail-email"
  | "calendar-event"
  | "teams-message"
  | "whatsapp-message"
  | "sms-message"
  | "obsidian-note"
  | "voice-transcript"
  | "rss-item"
  | "linkedin"
  | "manual-note"
  | "generic";

export interface PacketSourceHeader {
  from?: string | null;
  fromAddress?: string | null;
  to?: string | null;
  cc?: string | null;
  bcc?: string | null;
  subject?: string | null;
  when?: string | null;
  organizer?: string | null;
  attendees?: string[];
  location?: string | null;
  durationMinutes?: number | null;
  threadTopic?: string | null;
  participants?: string[];
  channelName?: string | null;
}

export interface PacketSourceAttachment {
  filename: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  isInline?: boolean;
}

export interface PacketSourceBody {
  html?: string | null;
  text?: string | null;
  snippet?: string | null;
}

export interface PacketSourceActions {
  canOpenExternal: boolean;
  externalUrl: string | null;
  canSnooze: boolean;
  canDraftReply: boolean;
  canArchive: boolean;
  canMarkComplete: boolean;
  canForward: boolean;
}

export interface PacketSourceConnector {
  kind: string;
  displayName: string;
  connected: boolean;
  reauthHint: string | null;
}

export interface PacketSourceResolved {
  packet: {
    id: string;
    source_kind: string | null;
    external_id: string | null;
    captured_at: string;
    direction: string | null;
    sensitivity_tier: string | null;
    type: string | null;
    source: string | null;
    thread_key: string | null;
    engagement_id: string | null;
    meta: Record<string, unknown> | null;
  };
  render: {
    kind: PacketRenderKind;
    header: PacketSourceHeader;
    body: PacketSourceBody;
    attachments: PacketSourceAttachment[];
  };
  connector: PacketSourceConnector;
  actions: PacketSourceActions;
  snooze: { snoozed: boolean; until_at: string | null };
  fetch: { status: "live" | "cached" | "stub"; note?: string };
}

export interface IntentRow {
  intent_id: string;
  kind: string;
  subject: string;
  context_quote: string | null;
  status: string;
  priority_hint: number;
  surface_strategy: string | null;
  source_packet_id: string | null;
  captured_at: string | null;
  due_at: string | null;
  surfaced_count: number;
  last_surfaced_at: string | null;
  completed_at: string | null;
  notes: string | null;
  created_at: string;
  has_research?: number;
}

export interface ResearchResult {
  result_id: string;
  intent_id: string;
  query: string;
  summary: string | null;
  sources: Array<{ title: string; url: string; snippet: string }>;
  key_facts: string[];
  computed_at: string;
}

export interface ViewDef {
  viewId: string;
  name: string;
  slug: string;
  scopes: string[];
  isBuiltin: boolean;
  isDefault: boolean;
  isEverything: boolean;
  sortOrder: number;
}

export interface ScopeDef {
  scope: string;
  display_name: string;
  description: string | null;
  sort_order: number;
}

export interface TagRow {
  id: string;
  user_id: string;
  target_kind: "capture" | "person" | "event";
  target_id: string;
  name: string;
  confirmed: number;
  inference_reason: string | null;
  inference_source: string | null;
  inference_confidence: number | null;
  confirmed_at: string | null;
  rejected_at: string | null;
  created_at: string;
}

export interface IdentityNodeBrief {
  id: string;
  label: string;
  merged_into_id: string | null;
}
export interface IdentityMergeCandidate {
  id: string;
  merge_score: number;
  status: string;
  created_at: string;
  similarity: Record<string, number>;
  person_a: IdentityNodeBrief | null;
  person_b: IdentityNodeBrief | null;
}
export interface IdentityLinkRow {
  id: string;
  source_kind: string;
  source_id: string;
  display_name_at_link: string | null;
  confidence: number;
  confirmed_at: string | null;
  created_at: string;
}

export interface MergeCandidateRow {
  id: string;
  user_id: string;
  person_a_id: string;
  person_b_id: string;
  status: "pending" | "confirmed" | "rejected";
  confirmed: number;
  inference_reason: string | null;
  inference_source: string | null;
  inference_confidence: number | null;
  confirmed_at: string | null;
  rejected_at: string | null;
  created_at: string;
  person_a?: MergeCandidatePerson | null;
  person_b?: MergeCandidatePerson | null;
}

export interface MergeCandidatePerson {
  id: string;
  display_name: string;
  primary_email: string | null;
  organization: string | null;
  role?: string | null;
  avatar_url: string | null;
}

export interface MergeExecutionResult {
  candidate: MergeCandidateRow;
  keptPersonId: string;
  removedPersonId: string;
  reassignedInteractions: number;
  reassignedRelationships: number;
  reassignedFieldSources: number;
  error?: string;
}

export interface NeedsReviewResponse {
  stats: {
    totalPeople: number;
    totalRelationships: number;
    totalTags: number;
    totalScopes: number;
    totalMerges: number;
    totalAll: number;
  };
  people: Array<{
    id: string;
    display_name: string;
    primary_email: string | null;
    organization: string | null;
    role: string | null;
    avatar_url: string | null;
    last_interacted_at: string | null;
    source: string;
  }>;
  relationships: Array<{
    id: string;
    person_id: string;
    type: string;
    inference_reason: string | null;
    inference_source: string | null;
    inference_confidence: number | null;
    created_at: string;
    display_name: string;
    primary_email: string | null;
    organization: string | null;
    avatar_url: string | null;
  }>;
  tags: TagRow[];
  scopes: Array<{
    id: string;
    captured_at: string;
    type: string;
    scope: string;
    text: string;
    scope_inference_reason: string | null;
    scope_inference_source: string | null;
    scope_inference_confidence: number | null;
  }>;
  merges: MergeCandidateRow[];
}

export type PeopleSort = "strength" | "recent" | "alpha" | "frequent";

/** Legacy + granular taxonomy. The backend's full taxonomy is open;
 *  the SPA can render any string returned by the API. This list
 *  exists only so the type system catches typos in legacy call
 *  sites; new code uses string. */
export type RelationshipType =
  | "colleague" | "family" | "friend" | "vendor" | "client" | "manager"
  | "report" | "other"
  | "wife" | "husband" | "partner" | "ex_wife" | "ex_husband"
  | "mother" | "father" | "parent" | "stepmother" | "stepfather"
  | "daughter" | "son" | "child"
  | "sister" | "brother" | "sibling"
  | "grandmother" | "grandfather" | "grandparent"
  | "aunt" | "uncle" | "cousin" | "niece" | "nephew"
  | "mother_in_law" | "father_in_law" | "sister_in_law" | "brother_in_law"
  | "skip_level_manager" | "direct_report" | "skip_level_report"
  | "peer" | "mentor" | "mentee"
  | "business_partner" | "co_founder" | "investor" | "advisor"
  | "customer" | "supplier" | "contractor"
  | "recruiter" | "candidate"
  | "doctor" | "dentist" | "therapist" | "lawyer" | "accountant" | "financial_advisor"
  | "hairdresser" | "mechanic" | "contractor_home" | "realtor" | "landlord"
  | "neighbor" | "roommate"
  | "family_friend" | "best_friend" | "close_friend" | "acquaintance"
  | "ex_partner" | "dating" | "girlfriend" | "boyfriend" | "ex_girlfriend" | "ex_boyfriend"
  | "teacher" | "professor" | "classmate" | "student"
  | "religious_leader" | "coach" | "trainer"
  | "unknown" | "custom"
  | string;

/** One node of the hierarchical relationship taxonomy. Mirrors the
 *  backend TaxonomyEntry (relationship-taxonomy.ts) returned by
 *  GET /api/relationships/taxonomy. */
export interface TaxonomyEntry {
  type: string;
  parent_type: string | null;
  display_name: string;
  short_name: string;
  gender_hint: "female" | "male" | null;
  priority_weight: number | null;
  sort_order: number;
  is_user_facing: 0 | 1;
}

export interface TaxonomyResponse {
  /** Flat slug list (back-compat). */
  taxonomy: string[];
  /** Full hierarchy with weights / labels / gender. */
  tree: TaxonomyEntry[];
  /** tree pre-filtered to is_user_facing === 1 (for pickers). */
  userFacing: TaxonomyEntry[];
}

/** An auto-flip the evidence ledger made (drives the "Memso updated
 *  this" pill). */
export interface RelationshipRevision {
  revision_id: string;
  person_id: string;
  display_name: string;
  was_type: string | null;
  now_type: string | null;
  reason: string | null;
  evidence_packet_ids: string | null;
  decided_at: string;
  user_acked_at: string | null;
  user_action: "accepted" | "reverted" | null;
}

/** A parked proposal: contradicting evidence vs a user-confirmed
 *  value (the lock case, surfaced as "Memso noticed"). */
export interface RelationshipPending {
  pending_id: string;
  person_id: string;
  display_name: string;
  was_type: string | null;
  proposed_type: string | null;
  reason: string | null;
  created_at: string;
}

export interface PersonInferredRelationship {
  id: string;
  type: string;
  /** Tentative more-specific guess when confidence was too low to
   *  commit it to `type` (e.g. type=spouse, inferred_subtype=wife). */
  inferred_subtype: string | null;
  label_custom: string | null;
  confirmed: boolean;
  /** 'user_inline' = user confirmed; 'auto_confirmed' = Memso
   *  auto-confirmed above threshold (still show a transparency
   *  footer); others = inferer source. */
  inference_source: string | null;
  inference_confidence: number | null;
  inference_reasoning: string | null;
  inference_evidence: string[];
  inferred_at: string | null;
  inference_model: string | null;
}

export interface Person {
  id: string;
  display_name: string;
  primary_email: string | null;
  alt_emails: string[];
  phone: string | null;
  alt_phones: string[];
  whatsapp_jid: string | null;
  teams_mri: string | null;
  /** Surfaces this contact has appeared on. ["gmail","calendar","whatsapp"] */
  sources_seen: string[];
  avatar_url: string | null;
  role: string | null;
  organization: string | null;
  notes: string | null;
  last_interacted_at: string | null;
  source: string;
  created_at: string;
  updated_at: string;
  interactionCount?: number;
  /** Top relationship row (confirmed-first, then highest-confidence
   *  inferred). Null when the contact has no relationship yet. */
  relationship?: PersonInferredRelationship | null;
  /** True once the user has tapped Confirm on the contact at least
   *  once. Pre-confirmation contacts render with a dashed amber
   *  avatar ring; post-confirmation render solid. */
  is_fully_confirmed?: boolean;
  fully_confirmed_at?: string | null;
  /** Per-packet enrichment knowledge accumulated over time. */
  inferred_employer?: string | null;
  inferred_role?: string | null;
  inferred_team?: string | null;
  tone_to_user?: string | null;
  relationship_strength_score?: number | null;
  topics_associated?: string[];
  evidence_packet_count?: number;
  last_enrichment_at?: string | null;
}

export interface ContactContextSummary {
  evidence_packets: Array<{
    packet_id: string;
    captured_at: string;
    relationship: string;
    confidence: number;
    quote: string | null;
    employer: string | null;
    role: string | null;
    team: string | null;
    tone: string | null;
    addressing_pattern: string | null;
  }>;
  relationship_votes: Record<string, { count: number; max_confidence: number }>;
  signature_signals: {
    employer_votes: Record<string, number>;
    role_votes: Record<string, number>;
    team_votes: Record<string, number>;
  };
  hierarchical_signals: {
    likely_supervisor_count: number;
    likely_subordinate_count: number;
    likely_peer_count: number;
  };
  total_packets: number;
  most_recent_evidence_at: string | null;
}

export interface PersonInput {
  display_name?: string;
  primary_email?: string | null;
  alt_emails?: string[];
  role?: string | null;
  organization?: string | null;
  notes?: string | null;
  avatar_url?: string | null;
}

export interface PeopleListResponse {
  people: Person[];
  total: number;
  sort: PeopleSort;
  limit: number;
  offset: number;
}

export type PeopleFieldName =
  | "display_name"
  | "primary_email"
  | "alt_email"
  | "phone"
  | "role"
  | "organization"
  | "avatar_url"
  | "notes";

export interface FieldObservation {
  id: string;
  user_id: string;
  person_id: string;
  field_name: PeopleFieldName;
  value: string;
  source: string;
  confidence: number;
  confirmed: boolean;
  /** Set on observations that landed AFTER the contact was fully
   *  confirmed AND whose value conflicts with a confirmed value
   *  for the same field. The SPA uses this to pick fields needing
   *  attention without re-rendering the whole inferred-state UI. */
  field_change_pending: boolean;
  observed_at: string;
  confirmed_at: string | null;
  rejected_at: string | null;
  meta_json: Record<string, unknown> | null;
}

export interface PersonFieldsResponse {
  observations: FieldObservation[];
  byField: Partial<Record<PeopleFieldName, FieldObservation[]>>;
  inferredCount: number;
  pendingChangeCount: number;
  pendingFields: PeopleFieldName[];
  isFullyConfirmed: boolean;
  fullyConfirmedAt: string | null;
}

export interface InteractionRow {
  id: string;
  user_id: string;
  person_id: string;
  kind: "email" | "calendar-event" | "note-mention" | "voice-mention";
  reference_id: string | null;
  summary: string | null;
  occurred_at: string;
  created_at: string;
}

export interface RelationshipRow {
  id: string;
  user_id: string;
  person_id: string;
  type: RelationshipType;
  notes: string | null;
  established_at: string | null;
  created_at: string;
  /** True when the user has explicitly confirmed this relationship.
   *  False rows are inferences from the relationship-inferer; the
   *  UI renders them dashed and offers Confirm / Edit / Reject. */
  confirmed?: boolean;
  inference_reason?: string | null;
  inference_source?: string | null;
  inference_confidence?: number | null;
  confirmed_at?: string | null;
  rejected_at?: string | null;
  relationship_label_custom?: string | null;
  inferred_subtype?: string | null;
  inference_evidence_json?: string | null;
  inference_reasoning?: string | null;
  inferred_at?: string | null;
  inference_model?: string | null;
  evidence_message_count?: number | null;
}

export interface PersonNoteMention {
  id: string;
  captured_at: string;
  text: string;
  type: string;
  scope: string;
}

export interface EngagementDetailResponse {
  engagement: {
    id: string;
    kind: string;
    title: string;
    description: string | null;
    status: string;
    scope: string | null;
    due_at: string | null;
    related_people_json: string | null;
    source_packets_json: string | null;
    detection_source: string;
    detection_reason: string | null;
    last_activity_at: string | null;
    activity_count: number;
    created_at: string;
    updated_at: string;
  };
  activity: Array<{
    id: string;
    kind: string;
    summary: string;
    occurred_at: string;
    source_ref_json: string | null;
    payload_json: string | null;
  }>;
  weave_insights?: WeaveInsight[];
}

export interface NodeNote {
  node_id: string;
  node_kind: string;
  title: string;
  body_markdown: string;
  last_synthesized_at: string | null;
  last_user_edited_at: string | null;
  conflict_state: string;
  obsidian_uri: string | null;
  vault_synced: boolean;
}

export interface SentimentWindow {
  window_start: string;
  window_end: string;
  sample_count: number;
  positive_ratio: number;
  neutral_ratio: number;
  negative_ratio: number;
  dominant_sentiment: string;
  trend: "improving" | "stable" | "declining" | "mixed";
  notable_topics: string[];
  scope_set: string[];
  last_evaluated_at: string;
}

export interface SentimentTrendResponse {
  person_id: string;
  windows: SentimentWindow[];
  latest: SentimentWindow | null;
}

export interface SentimentNeedsAttentionItem {
  person_id: string;
  display_name: string;
  window_end: string;
  sample_count: number;
  positive_ratio: number;
  negative_ratio: number;
  dominant_sentiment: string;
  trend: "improving" | "stable" | "declining" | "mixed";
  notable_topics: string[];
  reason: "declining_trend" | "persistently_negative";
}

export interface SentimentNeedsAttentionResponse {
  items: SentimentNeedsAttentionItem[];
}

export interface PersonDetailResponse {
  person: Person;
  interactions: InteractionRow[];
  relationships: RelationshipRow[];
  noteMentions: PersonNoteMention[];
  contact_context?: ContactContextSummary | null;
  weave_insights?: WeaveInsight[];
}

export interface WeaveInsight {
  id: string;
  user_id: string;
  kind: string;
  title: string;
  body: string | null;
  confidence: number | null;
  source_packet_ids: string | null;
  related_entity_kind: string | null;
  related_entity_id: string | null;
  payload_json: string | null;
  status: string;
  expires_at: string | null;
  refresh_count: number;
  created_at: string;
  updated_at: string;
  decided_at: string | null;
}

export interface RelationshipsResponse {
  relationships: Array<RelationshipRow & {
    display_name: string;
    primary_email: string | null;
    organization: string | null;
    role: string | null;
    avatar_url: string | null;
    last_interacted_at: string | null;
  }>;
  byType: Record<string, Array<{
    id: string;
    person: {
      id: string;
      display_name: string;
      primary_email: string | null;
      organization: string | null;
      role: string | null;
      avatar_url: string | null;
      last_interacted_at: string | null;
    };
    type: string;
    notes: string | null;
    established_at: string | null;
    created_at: string;
    confirmed: boolean;
    inference_reason: string | null;
    inference_source: string | null;
    inference_confidence: number | null;
    confirmed_at: string | null;
    rejected_at: string | null;
  }>>;
  counts: Record<string, number>;
  inferredCount: number;
}

/** Tagged error so callers can render specific UI (retry button +
 *  "check your connection" copy for network, "Memso engine error" for
 *  HTTP, etc.) instead of the raw browser-vendor fetch error string. */
export class ConversationStreamError extends Error {
  readonly kind: "network" | "http" | "timeout" | "parse";
  readonly status?: number;
  constructor(
    kind: "network" | "http" | "timeout" | "parse",
    message: string,
    status?: number,
  ) {
    super(message);
    this.kind = kind;
    this.status = status;
    this.name = "ConversationStreamError";
  }
}

async function runConversationStream(
  args: { query: string; conversation_id?: string; scope?: string },
  onEvent: (event: ConversationStreamEvent) => void,
): Promise<void> {
  // Client-side timeout for the FIRST byte (meta event). Once the
  // stream starts producing bytes the budget resets implicitly because
  // each reader.read() is its own await. 30s buys plenty of headroom
  // for retrieval (FTS5 + sqlite-vec + graph) and for the LLM start;
  // beyond that the connection is almost certainly dead and the user
  // benefits from a clear "tap retry" rather than indefinite spinner.
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 30_000);

  let res: Response;
  try {
    res = await fetch(`/api/conversation/query`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...csrfHeaders(),
      },
      credentials: "same-origin",
      body: JSON.stringify(args),
      signal: controller.signal,
    });
  } catch (err) {
    window.clearTimeout(timeoutId);
    // AbortController.abort() lands here as DOMException with name
    // "AbortError"; native fetch failures (offline, DNS, TLS, etc.)
    // surface as TypeError. Both should read as "couldn't reach
    // Memso", not the browser-vendor string.
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ConversationStreamError(
        "timeout",
        "Memso took too long to respond. Tap retry to try again.",
      );
    }
    throw new ConversationStreamError(
      "network",
      "Couldn't reach Memso. Check your connection and tap retry.",
    );
  }

  const seenVersion = res.headers.get("x-app-version");
  if (seenVersion) noteServerVersion(seenVersion);
  if (res.status === 401 && typeof window !== "undefined") {
    window.clearTimeout(timeoutId);
    if (!window.location.pathname.startsWith("/login")) {
      const next = window.location.pathname + window.location.search;
      window.location.replace(`/login?next=${encodeURIComponent(next)}`);
      throw new ConversationStreamError("http", "Redirecting to login", 401);
    }
  }
  if (!res.ok || !res.body) {
    window.clearTimeout(timeoutId);
    const text = await res.text().catch(() => "");
    throw new ConversationStreamError(
      "http",
      text || `Memso responded with status ${res.status}. Tap retry to try again.`,
      res.status,
    );
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let firstByteSeen = false;
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      if (!firstByteSeen) {
        // Server is alive and emitting; cancel the first-byte budget.
        firstByteSeen = true;
        window.clearTimeout(timeoutId);
      }
      buf += decoder.decode(value, { stream: true });
      let sep = buf.indexOf("\n\n");
      while (sep !== -1) {
        const block = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        sep = buf.indexOf("\n\n");
        const parsed = parseSseBlock(block);
        if (parsed) onEvent(parsed);
      }
    }
  } catch (err) {
    window.clearTimeout(timeoutId);
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ConversationStreamError(
        "timeout",
        "Memso went quiet mid-stream. Tap retry.",
      );
    }
    throw new ConversationStreamError(
      "network",
      "Connection to Memso dropped mid-stream. Tap retry.",
    );
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function parseSseBlock(block: string): ConversationStreamEvent | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const raw of block.split("\n")) {
    const line = raw.trimEnd();
    if (!line || line.startsWith(":")) continue;
    const idx = line.indexOf(":");
    if (idx === -1) continue;
    const field = line.slice(0, idx);
    const value = line.slice(idx + 1).replace(/^\s/, "");
    if (field === "event") event = value;
    else if (field === "data") dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  const dataStr = dataLines.join("\n");
  let data: unknown;
  try {
    data = JSON.parse(dataStr);
  } catch {
    return null;
  }
  switch (event) {
    case "meta":
      return { kind: "meta", data: data as ConversationStreamMeta };
    case "retrieval":
      return {
        kind: "retrieval",
        data: (Array.isArray(data) ? data : []) as ConversationRetrievalItem[],
      };
    case "entity_aware":
      return { kind: "entity_aware", data: data as EntityAwarePayload };
    case "token":
      return { kind: "token", data: data as { delta: string } };
    case "done":
      return { kind: "done", data: data as ConversationStreamDone };
    case "error":
      return { kind: "error", data: data as { message: string } };
    default:
      return null;
  }
}

export interface ConversationStreamMeta {
  conversation_id: string;
  model: string;
  route: "local" | "anthropic" | "claude-session";
  forced: boolean;
  fallbackReason: string | null;
  temporalLabel: string | null;
}

export interface ConversationStreamDone {
  message_id: string;
  response: string;
  degradation: string | null;
}

export interface EntityAwareCitation {
  ref_kind: string;
  ref_id: string;
  label: string;
  date: string | null;
}

export interface EntityAwareAnchor {
  kind: "person" | "place" | "org" | "project" | "topic";
  label: string;
}

export interface EntityAwareResolved {
  id: string;
  type: string;
  display_name: string;
}

export interface EntityAwarePayload {
  intent:
    | "count"
    | "when_last"
    | "when_first"
    | "who"
    | "where"
    | "list"
    | "dossier";
  anchors: EntityAwareAnchor[];
  resolved: EntityAwareResolved[];
  citations: EntityAwareCitation[];
  aggregation: Record<string, unknown>;
  answer_source: "llm" | "deterministic";
}

export type ConversationStreamEvent =
  | { kind: "meta"; data: ConversationStreamMeta }
  | { kind: "retrieval"; data: ConversationRetrievalItem[] }
  | { kind: "entity_aware"; data: EntityAwarePayload }
  | { kind: "token"; data: { delta: string } }
  | { kind: "done"; data: ConversationStreamDone }
  | { kind: "error"; data: { message: string } };

export interface ConversationRetrievalSource {
  store: "semantic" | "fts" | "graph" | "temporal";
  rank: number;
  rawScore?: number;
}

export interface ConversationRetrievalItem {
  id: string;
  type: string;
  snippet: string;
  scope: string;
  created_at: string;
  last_accessed_at: string | null;
  score: number;
  sources: ConversationRetrievalSource[];
}

export interface ConversationQueryResponse {
  conversation_id: string;
  message_id: string;
  response: string;
  retrieval: ConversationRetrievalItem[];
  model: string;
  route: "local" | "anthropic" | "claude-session";
  forced: boolean;
  fallbackReason: string | null;
  degradation: string | null;
  temporalLabel: string | null;
}

export interface ConversationSummary {
  id: string;
  title: string | null;
  created_at: string;
  last_message_at: string;
}

export interface ConversationDetailMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  retrieval: ConversationRetrievalItem[];
  rating: number | null;
  rating_comment: string | null;
  model: string | null;
  route: string | null;
  created_at: string;
}

export interface ConversationDetail {
  id: string;
  title: string | null;
  created_at: string;
  last_message_at: string;
  messages: ConversationDetailMessage[];
}

export interface VaultEntry {
  name: string;
  path: string;
  isDir: boolean;
  size?: number;
  mtime?: string;
}

export interface ExploreDocument {
  id: string;
  capture_id: string | null;
  filename: string;
  mime: string | null;
  byte_size: number | null;
  scope: string;
  created_at: string;
}

export interface ExploreMedia {
  id: string;
  filename: string;
  mime: string;
  byte_size: number | null;
  scope: string;
  created_at: string;
}

export interface ExploreQuote {
  id: string;
  text: string;
  attribution: string | null;
  scope: string;
  created_at: string;
}

export interface BrainstormTurnResponse {
  sessionId: string;
  assistant: string;
  citations: Array<{ captureId: string; snippet: string; scope: string; capturedAt: string; score: number }>;
  model: string;
  route: "local" | "anthropic" | "claude-session";
  forced: boolean;
  fallbackReason: string | null;
}

export interface BrainstormSessionSummary {
  session_id: string;
  started_at: string;
  last_at: string;
  turns: number;
}

export interface GraphResponse {
  nodes: Array<{ id: string; kind: string; name: string; scope: string }>;
  edges: Array<{ id: string; from_entity: string; to_entity: string; kind: string }>;
}

export interface Recommendation {
  id: string;
  source_agent: string;
  kind: string;
  scope: string;
  title: string;
  body_md: string | null;
  confidence: number | null;
  status: string;
  created_at: string;
  decided_at: string | null;
  executed_at?: string | null;
  execution_result_json?: string | null;
  meta_json?: string | null;
  payload_json?: string | null;
  surface?: string | null;
  exec_status?: string | null;
}

export interface ActionExecutionResult {
  success: boolean;
  summary: string;
  links: Array<{ label: string; url: string }>;
  details_json: Record<string, unknown> | null;
}

export interface CurationScoreComponent {
  label: string;
  met: boolean;
}

export interface CurationSurfaceScore {
  surface: string;
  name: string;
  curate: number;
  dynamic: number;
  learning: number;
  components: {
    curate: CurationScoreComponent[];
    dynamic: CurationScoreComponent[];
    learning: CurationScoreComponent[];
  };
}

export interface SuppressedSender {
  id: string;
  sender: string;
  kind: string;
  suppression_kind: "skip_threshold" | "forever";
  skip_count: number;
  last_skip_at: string | null;
  suppressed_at: string | null;
  expires_at: string | null;
  reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActionAuditEntry {
  id: string;
  action_kind: string;
  payload_json: string | null;
  decided_at: string;
  decided_by: string;
  outcome: string;
  outcome_json: string | null;
}

export interface ComposerRunResponse {
  ok: boolean;
  text: string;
  kind: string;
  model: string;
  intended_model: string;
  route: "local" | "anthropic" | "claude-session";
  degradation: string | null;
  fallback_reason: "privacy" | "missing-api-key" | "session-unavailable" | null;
  scopes: string[];
  context_packet_ids: string[];
  usage: { input_tokens: number; output_tokens: number; latency_ms: number };
}

export type PrivacyScope =
  | "private"
  | "work"
  | "business"
  | "personal"
  | "family"
  | "social"
  | "financial"
  | "side_project";

export interface ConnectorInstance {
  id: string;
  kind: string;
  account: string;
  enabled: number;
  scope: string;
  primary_scope?: string | null;
  sub_scope?: string | null;
  pending_scope_picker?: number;
  privacy_label?: string | null;
  config_json: string | null;
  last_sync_at: string | null;
  last_status: string | null;
  /** Packets captured into this instance over the last 30 days
   *  (live window). Best-effort metric from the backend. */
  packet_count_30d?: number;
  /** Distinct engagements that this instance's packets feed. */
  engagement_count?: number;
}

export interface ConnectorRegisteredEntry {
  kind: string;
  /** Friendlier label for canonical-services entries (e.g. "Outlook
   *  (Exchange / Office 365)"); registry kinds use the kind itself. */
  label?: string;
  description: string;
  configFields: Array<{ key: string; label: string; type: string; placeholder?: string }>;
  oauthFlow: "google" | "github" | null;
  supportsMultipleInstances?: boolean;
  /** "registry" for entries from CONNECTOR_REGISTRY (rss / github /
   *  gmail / calendar). "canonical" for the wizard-driven services
   *  (outlook / teams / whatsapp). */
  kindSource?: "registry" | "canonical";
  /** Canonical-only: pre-selected scope when the user starts the
   *  setup wizard (still re-asked at create). */
  defaultPrivacyScope?: PrivacyScope;
  /** Canonical-only: tailwind color family for the kind chip. */
  badgeColor?: "indigo" | "rose" | "emerald" | "amber" | "sky" | "purple";
  /** Canonical-only: numbered setup wizard. */
  setupSteps?: Array<{ title: string; body: string; cta?: { label: string; href?: string; command?: string } }>;
  /** Canonical-only: deep-link to the per-feature settings page once
   *  the connector is configured. */
  settingsHref?: string;
  /** Canonical-only: URL the SPA can hit to read live health. */
  healthProbeHref?: string | null;
}

export interface RoadmapConnectorEntry {
  slug: string;
  label: string;
  description: string;
  privacyHint: "work" | "personal" | "family" | "shared" | "finance" | "knowledge";
  plannedFor: "V2.x" | "V2.1" | "V2.2" | "V2.3" | "V2.4" | "V3";
  category: "work" | "personal" | "family" | "knowledge" | "finance";
  starred: boolean;
}

export interface ConnectorsResponse {
  connectors: ConnectorInstance[];
  registered: ConnectorRegisteredEntry[];
  /** Pre-grouped instances keyed by connector kind for the
   *  Connected tab's nested rendering ("Gmail (2 accounts)" with
   *  both instances inside). */
  instances_by_kind?: Record<string, ConnectorInstance[]>;
  /** Future / planned connectors surfaced on the Available tab as
   *  "Coming soon" cards with star-to-prioritise interaction. */
  roadmap?: RoadmapConnectorEntry[];
}

export interface WorkConnectorHealth {
  kind: string;
  displayName: string;
  enabled: boolean;
  health: "green" | "yellow" | "red";
  lastRunAt: string | null;
  lastRunStatus: string | null;
}

export type ConnectorCatalogStatus =
  | "built"
  | "in_progress"
  | "planned"
  | "request_only"
  | "deprecated";

export interface ConnectorCatalogEntry {
  slug: string;
  display_name: string;
  category: string;
  description: string | null;
  icon_url: string | null;
  status: ConnectorCatalogStatus;
  privacy_scope: string | null;
  privacy_tier: string;
  auth_kind: string | null;
  config_route: string | null;
  github_repo: string | null;
  popularity: number;
  is_recommended: boolean;
  connected: boolean;
  last_sync_at: string | null;
  last_status: string | null;
  requested: boolean;
  /** Lifecycle state for data_export_import connectors. null until
   *  the user has run the Configure ("Set up") step. */
  instance: {
    configured: boolean;
    account_label: string;
    account_identifier: string | null;
    scope: string;
    last_import_at: string | null;
    last_import_items: number | null;
    has_done_export: boolean;
  } | null;
}

export interface ConnectorCatalogCategory {
  slug: string;
  display_name: string;
  description: string | null;
  icon: string | null;
  connector_count: number;
  connectors: ConnectorCatalogEntry[];
}

export interface ConnectorCatalogResponse {
  categories: ConnectorCatalogCategory[];
}

export interface BacklogJob {
  id: string;
  kind: string;
  status: string;
  total: number;
  processed: number;
  errors: number;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  user_id?: string | null;
  progress?: number;
  paused_at?: string | null;
  estimate_json?: string | null;
  result_json?: string | null;
  data_json?: string | null;
}

export interface BacklogEstimate {
  items: number;
  tokens_estimate: number;
  cost_usd: number;
  eta_minutes: number;
  summary: string;
  details?: Record<string, unknown>;
}

export interface IngestionPreview {
  ok: boolean;
  intent: string;
  rationale: string;
  rewritten: string;
  metaHints: { url?: string; personName?: string; watchlistName?: string; scheduleHint?: string };
  meta: { model: string; route: string };
}

export interface AgentSummary {
  name: string;
  default: string;
  effective: string;
  localFallback: string;
  description: string;
  opusEscalateAvailable: boolean;
  opusEscalateAlwaysOn: boolean;
  modelOverride: string | null;
  prefersClaudeSession: boolean;
  todaySpend: { local: { runs: number; tokens: number }; anthropic: { runs: number; tokens: number }; "claude-session": { runs: number; tokens: number } };
}

export function setAgentOverride(name: string, patch: { modelOverride?: string | null; opusEscalateAlwaysOn?: boolean }): Promise<{ ok: boolean }> {
  return fetch(`/api/agents/${encodeURIComponent(name)}/override`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...csrfHeaders() },
    credentials: "same-origin",
    body: JSON.stringify(patch),
  }).then((r) => json(r) as Promise<{ ok: boolean }>);
}

export type ObservationKind =
  | "tone_shift"
  | "relationship_warming"
  | "pattern_anomaly"
  | "topic_emergence"
  | "commitment_overdue"
  | "decision_revisit"
  | "question_unresolved"
  | "frequency_unusual"
  | "birthday_upcoming"
  | "deadline_approaching";

export type ObservationStatus = "new" | "seen" | "archived" | "false_positive";

export interface Observation {
  id: string;
  user_id: string;
  kind: ObservationKind;
  title: string;
  body: string | null;
  confidence: number | null;
  source_packet_ids: string[];
  related_entity_kind: string | null;
  related_entity_id: string | null;
  status: ObservationStatus;
  payload: Record<string, unknown> | null;
  created_at: string;
  seen_at: string | null;
  archived_at: string | null;
  marked_false_at: string | null;
  is_test: number;
  /** Phase 2: the cleanup action handler kind the pattern-detector
   *  suggests for this observation (null when no cleanup applies). */
  suggested_action_kind?: string | null;
}

export interface ObservationPreference {
  kind: ObservationKind;
  muted: boolean;
  updatedAt: string | null;
}

export interface PrivateRevision {
  id: string;
  packetId: string;
  wasScope: string | null;
  nowScope: string;
  reason: string;
  detector: string;
  status: "applied" | "pending";
  userAction: "kept_private" | "reclassified" | null;
  reclassifiedTo: string | null;
  createdAt: string;
  reviewedAt: string | null;
  sourceKind: string | null;
  preview: string;
}

export interface AboutYouFacet {
  facet: string;
  headline: string | null;
  confidence: number;
  scope: string;
  computed_at: string;
  status: string;
  data: Record<string, unknown>;
}
